import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  Checkbox,
  ListItemText,
  ListItemIcon,
  TableSortLabel,
  FormControlLabel,
  Switch,
  Popover,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircleIcon from '@mui/icons-material/Circle';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CallIcon from '@mui/icons-material/Call';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import MonitorIcon from '@mui/icons-material/Monitor';
import CloseIcon from '@mui/icons-material/Close';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import { useApi } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { usePermissions } from '../hooks/usePermissions';
import AgentDetailsModal from '../components/supervisor/AgentDetailsModal';
import AgentQuickPanel from '../components/supervisor/AgentQuickPanel';
import {
  AGENT_STATUS_ORDER,
  getAgentStatusLabel,
  getAgentStatusColor,
  getAgentStatusGradient,
  getAgentStatusNeonColor,
  getAgentStatusTextColor
} from '../constants/agentStatus';
import * as d3 from 'd3';

const COLUMN_OPTIONS = [
  { value: 'indicator', label: 'Indicador', header: '', sortable: false, filterable: false, type: 'icon' },
  { value: 'agentId', label: 'ID', header: 'ID', sortable: true, filterable: true, type: 'string' },
  { value: 'name', label: 'Nombre', header: 'Nombre', sortable: true, filterable: true, type: 'string' },
  { value: 'extension', label: 'Extensión', header: 'Extensión', sortable: true, filterable: true, type: 'string' },
  { value: 'status', label: 'Estado', header: 'Estado', sortable: true, filterable: true, type: 'string' },
  { value: 'stateChanges', label: 'Cambios', header: 'Cambios de estado', sortable: true, filterable: false, type: 'number' },
  { value: 'callCount', label: 'Llamadas', header: 'Llamadas hoy', sortable: true, filterable: false, type: 'number' },
  { value: 'time', label: 'Tiempo en estado', header: 'Tiempo en estado', sortable: true, filterable: false, type: 'number' },
  { value: 'connectionTime', label: 'Tiempo conectado', header: 'Tiempo conectado', sortable: true, filterable: false, type: 'number' },
  { value: 'firstConnection', label: 'Primera conexión', header: 'Primera conexión', sortable: true, filterable: false, type: 'datetime' },
  { value: 'lastDisconnect', label: 'Última desconexión', header: 'Última desconexión', sortable: true, filterable: false, type: 'datetime' },
  { value: 'skills', label: 'Skills', header: 'Skills', sortable: false, filterable: true, type: 'string' },
  { value: 'campaigns', label: 'Campañas', header: 'Campañas', sortable: false, filterable: true, type: 'string' }
];

const DEFAULT_VISIBLE_COLUMNS = COLUMN_OPTIONS.map(option => option.value);
const COLUMN_CONFIG = COLUMN_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option;
  return acc;
}, {});
const ONLINE_STATUS_LIST = [
  'AVAILABLE',
  'BUSY',
  'PAUSE',
  'NOTREADY',
  'ABSENT',
  'BREAK',
  'BATHROOM',
  'LUNCH',
  'REST',
  'TRAINING'
];
const ONLINE_STATUS_SET = new Set(ONLINE_STATUS_LIST);const LISTENABLE_STATUSES = new Set(['BUSY','INCALL']);
const SYSTEM_MANAGED_STATUSES = ['BUSY', 'INCALL', 'ACW', 'PRECALL', 'OFFLINE', 'NO_NETWORK', 'NOTREADY'];
const HARD_RESET_STATUSES = new Set([
  'PAUSE',
  'BREAK',
  'BATHROOM',
  'LUNCH',
  'REST',
  'TRAINING',
  'ABSENT',
  'BUSY',
  'INCALL',
  'ACW',
  'PRECALL',
  'PREVIEW',
  'NOTREADY_MANUAL'
]);

const FILTER_CONTROL_SX = {
  '& .MuiInputBase-root': {
    minHeight: 34,
    fontSize: '0.9rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem'
  },
  '& .MuiSelect-select': {
    py: 0.4
  }
};

const FILTER_LABEL_SX = {
  '& .MuiFormControlLabel-label': {
    fontSize: '0.85rem'
  }
};

const KPI_CARD_SX = {
  border: '1px solid',
  borderColor: 'divider'
};

const KPI_CARD_CONTENT_SX = {
  minHeight: 92,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 0.75
};

const formatDuration = (seconds = 0) => {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((safeSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(safeSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${hours}:${minutes}:${secs}`;
};

const formatDateTime = (value) => {
  if (!value) return '--';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return '--';
  }
};

const getStatusDurationSeconds = (agent, referenceNow) => {
  // Si el backend envió el acumulado de AVAILABLE, úsalo como base y suma el delta desde el snapshot
  if (agent?.status === 'AVAILABLE' && typeof agent?.availableSecondsToday === 'number') {
    const base = Math.max(0, Math.floor(agent.availableSecondsToday));
    const snapshotMs = typeof agent?.snapshotMs === 'number' ? agent.snapshotMs : null;
    const delta = snapshotMs ? Math.max(0, Math.floor((referenceNow - snapshotMs) / 1000)) : 0;
    console.debug('[SUPERVISOR][DURATION] AVAILABLE base+delta', {
      agent: agent?.agent,
      base,
      delta,
      snapshotMs,
      referenceNow
    });
    return base + delta;
  }

  if (agent?.lastStatusChangeMs) {
    return Math.max(0, Math.floor((referenceNow - agent.lastStatusChangeMs) / 1000));
  }
  return agent?.statusDurationSecondsBase || 0;
};

const getConnectionSeconds = (agent, referenceNow) => {
  const base = Number(agent?.connectionSecondsBase || 0);
  if (!agent?.currentOnlineSinceMs) {
    return base;
  }
  const delta = Math.max(0, Math.floor((referenceNow - agent.currentOnlineSinceMs) / 1000));
  return base + delta;
};

const isOnlineStatus = (status) => ONLINE_STATUS_SET.has(status);
const isAgentRole = (agent) => {
  const rawRole =
    agent?.role?.role_name ||
    agent?.role?.name ||
    agent?.role_name ||
    agent?.role ||
    '';
  if (!rawRole) {
    return false;
  }
  const normalized = String(rawRole).trim().toLowerCase();
  return normalized === 'agent' || normalized === 'agente';
};


const computeBubbleRadius = (agent, referenceNow) => {
  if (!agent) return 15;
  return 15 + Math.min(getStatusDurationSeconds(agent, referenceNow) / 15, 40);
};

const computeBubbleNameFontSize = (radius) => Math.max(10, Math.min(radius / 2.5, 18));
const computeBubbleTimeFontSize = (radius) => Math.max(8, Math.min(radius * 0.35, 14));

const truncateBubbleLabel = (text = '', radius) => {
  const fontSize = computeBubbleNameFontSize(radius);
  const maxChars = Math.max(6, Math.floor((radius * 2) / fontSize * 1.5));
  if (text.length <= maxChars) {
    return text;
  }
  return `${text.substring(0, maxChars - 1)}...`;
};

const extractSkillInfo = (skill) => {
  if (typeof skill === 'object' && skill !== null) {
    const id = skill.id || skill.skillId || skill.skilldef || JSON.stringify(skill);
    const name = skill.name || skill.skillname || skill.skillName || `Skill ${id}`;
    return { id: String(id), name };
  }
  const id = String(skill);
  return { id, name: `Skill ${id}` };
};

const buildSkillGrouping = (agents = []) => {
  const agentSkillsMap = {};
  const skillNamesMap = {};

  agents.forEach(agent => {
    const skillEntries = Array.isArray(agent.skills)
      ? agent.skills.filter(Boolean)
      : agent.skill
        ? [agent.skill]
        : [];

    if (!skillEntries.length) {
      if (!agentSkillsMap.default) {
        agentSkillsMap.default = [];
        skillNamesMap.default = 'Sin Skills';
      }
      agentSkillsMap.default.push(agent);
      return;
    }

    skillEntries.forEach(skillEntry => {
      const { id, name } = extractSkillInfo(skillEntry);
      if (!agentSkillsMap[id]) {
        agentSkillsMap[id] = [];
        skillNamesMap[id] = name;
      }
      agentSkillsMap[id].push(agent);
    });
  });

  if (!Object.keys(agentSkillsMap).length && agents.length > 0) {
    agentSkillsMap.default = agents;
    skillNamesMap.default = 'Sin Skills';
  }

  return { agentSkillsMap, skillNamesMap };
};

// Componente de Vista de Burbujas Espaciales
const SpaceBubbleView = ({
  agents,
  now,
  onContextMenu = () => {},
  immersive = false,
  onExitImmersive = () => {}
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const nodesRef = useRef(null);
  const skillAgentsRef = useRef({});
  const skillNamesRef = useRef({});
  const skillMetaRef = useRef({});
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const { skills: allSkills } = useApi();

  // Actualizar dimensiones del contenedor
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        // Asegurar que las dimensiones sean válidas antes de actualizar
        if (width > 0 && height > 0) {
          setDimensions({ width, height });
        }
      }
    };

    // Pequeño delay para asegurar que el contenedor esté renderizado
    const timer = setTimeout(() => {
      updateDimensions();
    }, 100);

    window.addEventListener('resize', updateDimensions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Inicializar la visualización D3 (solo una vez)
  useEffect(() => {
    if (!agents || agents.length === 0 || initialized) return;

    // Esperar a que las dimensiones sean válidas
    if (dimensions.width <= 0 || dimensions.height <= 0) return;

    const svg = d3.select(svgRef.current);
    const width = dimensions.width;
    const height = dimensions.height;

    // Limpiar SVG anterior
    svg.selectAll('*').remove();

    // Configurar SVG
    svg
      .attr('width', width)
      .attr('height', height)
      .style('background', 'radial-gradient(ellipse at center, #0a0e27 0%, #000000 100%)');

    // Crear grupo principal con zoom
    const g = svg.append('g');

    // Crear definiciones SVG para gradientes y filtros
    const defs = svg.append('defs');
    const nebulaLine = d3.line().curve(d3.curveCatmullRomClosed.alpha(0.7));

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    const computeClusterPositions = (containers, widthValue, heightValue) => {
      if (!containers.length) return {};
      const centers = {};
      const cx = widthValue / 2;
      const cy = heightValue / 2;
      const maxRadius = Math.max(...containers.map(c => c.radius || 60), 60);
      const total = containers.length;

      // Para un solo grupo, colocarlo en el centro
      if (total === 1) {
        centers[containers[0].skillId] = { x: cx, y: cy };
        return centers;
      }

      // Para 2-6 grupos, distribuir en círculo
      if (total <= 6) {
        const orbitPadding = maxRadius + 160;
        const orbitRadiusX = Math.max(orbitPadding, widthValue * 0.25);
        const orbitRadiusY = Math.max(orbitPadding * 0.9, heightValue * 0.2);
        containers.forEach((descriptor, index) => {
          const angle = (2 * Math.PI * index) / total - Math.PI / 2;
          centers[descriptor.skillId] = {
            x: cx + orbitRadiusX * Math.cos(angle),
            y: cy + orbitRadiusY * Math.sin(angle)
          };
        });
        return centers;
      }

      const columns = Math.min(4, Math.ceil(Math.sqrt(total)));
      const rows = Math.ceil(total / columns);
      const marginX = widthValue * 0.12;
      const marginY = heightValue * 0.15;
      const availableWidth = widthValue - marginX * 2;
      const availableHeight = heightValue - marginY * 2;
      const cellWidth = availableWidth / columns;
      const cellHeight = availableHeight / rows;

      containers.forEach((descriptor, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        centers[descriptor.skillId] = {
          x: marginX + cellWidth * (col + 0.5),
          y: marginY + cellHeight * (row + 0.5)
        };
      });

      return centers;
    };

    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i += 1) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
      }
      return hash;
    };

    const buildNebulaPath = (center, radius, seedValue, relative = false) => {
      const normalizedSeed = ((Math.abs(seedValue) % 2147483647) + 1) / 2147483648;
      const rng = d3.randomLcg(normalizedSeed);
      const points = [];
      const steps = 14;
      const baseX = relative ? 0 : center.x;
      const baseY = relative ? 0 : center.y;
      for (let i = 0; i < steps; i += 1) {
        const angle = (2 * Math.PI * i) / steps + (rng() - 0.5) * 0.4;
        const radial = radius * (0.7 + rng() * 0.6);
        points.push([
          baseX + radial * Math.cos(angle),
          baseY + radial * Math.sin(angle)
        ]);
      }
      return nebulaLine(points);
    };

    // Crear múltiples capas de estrellas para efecto parallax
    const starsLayer1 = svg.append('g').attr('class', 'stars-layer-1');
    const starsLayer2 = svg.append('g').attr('class', 'stars-layer-2');
    const starsLayer3 = svg.append('g').attr('class', 'stars-layer-3');

    // Capa 1 - Estrellas lejanas (se mueven menos con zoom)
    for (let i = 0; i < 100; i++) {
      starsLayer1.append('circle')
        .attr('cx', Math.random() * width * 2 - width * 0.5)
        .attr('cy', Math.random() * height * 2 - height * 0.5)
        .attr('r', Math.random() * 0.8)
        .attr('fill', 'white')
        .attr('opacity', Math.random() * 0.4 + 0.1)
        .style('animation', `twinkle ${5 + Math.random() * 5}s infinite`);
    }

    // Capa 2 - Estrellas medias
    for (let i = 0; i < 80; i++) {
      starsLayer2.append('circle')
        .attr('cx', Math.random() * width * 1.5 - width * 0.25)
        .attr('cy', Math.random() * height * 1.5 - height * 0.25)
        .attr('r', Math.random() * 1.2 + 0.3)
        .attr('fill', 'white')
        .attr('opacity', Math.random() * 0.6 + 0.2)
        .style('animation', `twinkle ${3 + Math.random() * 4}s infinite`);
    }

    // Capa 3 - Estrellas cercanas (se mueven más con zoom)
    for (let i = 0; i < 50; i++) {
      starsLayer3.append('circle')
        .attr('cx', Math.random() * width)
        .attr('cy', Math.random() * height)
        .attr('r', Math.random() * 1.5 + 0.5)
        .attr('fill', 'white')
        .attr('opacity', Math.random() * 0.8 + 0.2)
        .style('animation', `twinkle ${2 + Math.random() * 3}s infinite`);
    }

    // Añadir algunas nebulosas de fondo
    const nebulae = svg.append('g').attr('class', 'nebulae');
    for (let i = 0; i < 5; i++) {
      const gradient = defs.append('radialGradient')
        .attr('id', `nebula-${i}`);

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', `hsl(${Math.random() * 360}, 70%, 50%)`)
        .attr('stop-opacity', 0.2);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', `hsl(${Math.random() * 360}, 70%, 30%)`)
        .attr('stop-opacity', 0);

      nebulae.append('circle')
        .attr('cx', Math.random() * width)
        .attr('cy', Math.random() * height)
        .attr('r', 50 + Math.random() * 100)
        .attr('fill', `url(#nebula-${i})`)
        .style('mix-blend-mode', 'screen');
    }

    // Filtrar solo agentes conectados
    const connectedAgents = agents.filter(agent => agent.connection?.connected);

    const { agentSkillsMap, skillNamesMap } = buildSkillGrouping(connectedAgents);
    skillAgentsRef.current = agentSkillsMap;
    skillNamesRef.current = skillNamesMap;

    console.log('Skills detectados:', Object.keys(agentSkillsMap), skillNamesMap);

    // Calcular posiciones de los cúmulos con mejor distribución
    const skillGroups = Object.keys(agentSkillsMap);
    const numGroups = skillGroups.length;
    const centerX = width / 2;
    const centerY = height / 2;

    const marginX = width * 0.08;
    const marginY = height * 0.12;
    const availableWidth = Math.max(width - marginX * 2, width * 0.55);
    const availableHeight = Math.max(height - marginY * 2, height * 0.55);
    const gridCols = Math.ceil(Math.sqrt(numGroups));
    const gridRows = Math.ceil(numGroups / Math.max(gridCols, 1));

    const preparedNodes = {};
    const clusterMeta = {};

    skillGroups.forEach((skillId) => {
      const members = agentSkillsMap[skillId] || [];
      preparedNodes[skillId] = members.map((agent) => {
        const bubbleRadius = computeBubbleRadius(agent, now);
        return { agent, radius: bubbleRadius };
      });

      const maxRadius = preparedNodes[skillId].reduce((max, node) => Math.max(max, node.radius), 0);
      clusterMeta[skillId] = {
        maxRadius: maxRadius || 40,
        count: preparedNodes[skillId].length
      };
    });

    const containerDescriptors = skillGroups.map((skillId, groupIndex) => {
      const stats = clusterMeta[skillId] || { maxRadius: 40, count: 1 };
      const densityFactor = Math.sqrt(stats.count || 1) * 20;
      const radius = Math.max(220, stats.maxRadius + 160 + densityFactor);
      const col = groupIndex % Math.max(gridCols, 1);
      const row = Math.floor(groupIndex / Math.max(gridCols, 1));
      const cellWidth = availableWidth / Math.max(gridCols, 1);
      const cellHeight = availableHeight / Math.max(gridRows, 1);
      const initialX = marginX + cellWidth * (col + 0.5);
      const initialY = marginY + cellHeight * (row + 0.5);
      return { skillId, radius, x: initialX, y: initialY };
    });

    const placementMap = computeClusterPositions(containerDescriptors, width, height);
    const clusterCenters = {};
    containerDescriptors.forEach((descriptor) => {
      const placement = placementMap[descriptor.skillId] || { x: centerX, y: centerY };
      clusterCenters[descriptor.skillId] = placement;
    });

    const bubbleData = [];
    let globalNodeIndex = 0;
    const totalBubbles = Object.values(preparedNodes).reduce((sum, nodes) => sum + nodes.length, 0);

    skillGroups.forEach((skillId) => {
      const nodes = preparedNodes[skillId] || [];
      const center = clusterCenters[skillId] || { x: centerX, y: centerY };
      const totalNodes = Math.max(nodes.length, 1);

      nodes.forEach((node, agentIndex) => {
        const offsetAngle = (2 * Math.PI * agentIndex) / totalNodes;
        const offsetRadius = Math.min(70, 28 + node.radius * 0.45);

        // Posición inicial en el centro para la animación
        const initialAngle = (2 * Math.PI * globalNodeIndex) / totalBubbles;
        const initialRadius = Math.min(100, totalBubbles * 2);

        bubbleData.push({
          ...node.agent,
          skillId,
          clusterX: center.x,
          clusterY: center.y,
          // Posiciones iniciales en el centro formando un círculo
          x: centerX + initialRadius * Math.cos(initialAngle),
          y: centerY + initialRadius * Math.sin(initialAngle),
          // Posiciones finales objetivo
          targetX: center.x + offsetRadius * Math.cos(offsetAngle),
          targetY: center.y + offsetRadius * Math.sin(offsetAngle),
          radius: node.radius,
          color: getAgentStatusNeonColor(node.agent.status),
          statusLabel: getAgentStatusLabel(node.agent.status),
          id: `${node.agent.agent}-${skillId}`,
          initialAngle: initialAngle,
          nodeIndex: globalNodeIndex
        });
        globalNodeIndex++;
      });
    });

    const containerLookup = containerDescriptors.reduce((acc, descriptor) => {
      acc[descriptor.skillId] = descriptor.radius;
      return acc;
    }, {});

    const skillMeta = {};
    skillGroups.forEach((skillId, index) => {
      const center = clusterCenters[skillId] || { x: centerX, y: centerY };
      const title = `${skillNamesMap[skillId] || `Skill ${skillId}`} (${agentSkillsMap[skillId].length})`;
      const labelWidth = Math.min(280, Math.max(150, title.length * 8));
      const maxRadius = clusterMeta[skillId]?.maxRadius || 60;
      const nebulaRadius = Math.max(220, maxRadius + 180);
      const gradientId = `skill-nebula-${index}`;
      const blurId = `skill-nebula-blur-${index}`;
      const hue = (index * 75 + 160) % 360;
      // Crear path relativo al origen para animación
      const nebulaPath = buildNebulaPath(center, nebulaRadius, hashString(`${skillId}-${index}`), true);

      skillMeta[skillId] = {
        title,
        labelWidth,
        labelHeight: 30,
        labelGap: Math.max(80, (containerLookup[skillId] || nebulaRadius) * 0.4),
        nebulaRadius,
        hue,
        hueIndex: index,
        gradientId,
        blurId,
        nebulaPath,
        center,
        containerRadius: containerLookup[skillId] || nebulaRadius
      };
    });
    skillMetaRef.current = skillMeta;

    // Crear simulación de fuerzas con animación inicial
    const simulation = d3.forceSimulation(bubbleData)
      .force('collide', d3.forceCollide(d => d.radius + 5)) // Prevenir colisiones
      .alpha(1) // Empezar con máxima energía
      .alphaDecay(0); // Sin decaimiento inicial para mantener la animación

    // Variables para la animación
    let animationPhase = 'rotating'; // rotating -> expanding -> settling
    let rotationSpeed = 0.35; // Velocidad inicial de rotación (MÁS RÁPIDA)
    let animationStartTime = Date.now();
    let expansionProgress = 0;

    // Función de fuerza personalizada para mantener los cúmulos separados
    function forceCluster() {
      const strength = 0.2;
      let nodes;

      function force(alpha) {
        const l = alpha * strength;
        for (const d of nodes) {
          d.vx -= (d.x - d.clusterX) * l;
          d.vy -= (d.y - d.clusterY) * l;
        }
      }

      force.initialize = _ => nodes = _;
      force.strength = _ => strength;

      return force;
    }

    // Crear filtro de brillo neón
    const filter = defs.append('filter')
      .attr('id', 'neon-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    filter.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Configurar zoom y pan con efecto parallax (crear antes de usar)
    const zoom = d3.zoom()
      .scaleExtent([0.3, 5])
      .on('zoom', (event) => {
        const { transform } = event;

        // Aplicar transformación principal a las burbujas
        g.attr('transform', transform);

        // Efecto parallax para las capas de estrellas
        // Capa 1 (más lejana) se mueve menos
        starsLayer1.attr('transform',
          `translate(${transform.x * 0.3}, ${transform.y * 0.3}) scale(${1 + (transform.k - 1) * 0.2})`
        );

        // Capa 2 (media) movimiento moderado
        starsLayer2.attr('transform',
          `translate(${transform.x * 0.6}, ${transform.y * 0.6}) scale(${1 + (transform.k - 1) * 0.5})`
        );

        // Capa 3 (cercana) se mueve casi igual que el contenido principal
        starsLayer3.attr('transform',
          `translate(${transform.x * 0.9}, ${transform.y * 0.9}) scale(${1 + (transform.k - 1) * 0.8})`
        );

        // Nebulosas con movimiento suave
        nebulae.attr('transform',
          `translate(${transform.x * 0.4}, ${transform.y * 0.4}) scale(${1 + (transform.k - 1) * 0.3})`
        );
      });

    const applyInitialView = () => {
      if (!skillGroups.length) {
        // Si no hay grupos, centrar en el centro del viewport
        svg.call(
          zoom.transform,
          d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(1)
            .translate(-width / 2, -height / 2)
        );
        return;
      }

      // Usar el centro del viewport como fallback si los clusters no están posicionados
      const defaultCenter = { x: width / 2, y: height / 2 };

      const extents = skillGroups.map((skillId) => {
        const center = skillMeta[skillId]?.center || clusterCenters[skillId] || defaultCenter;
        const radius = skillMeta[skillId]?.containerRadius || skillMeta[skillId]?.nebulaRadius || 200;
        return {
          minX: center.x - radius,
          maxX: center.x + radius,
          minY: center.y - radius,
          maxY: center.y + radius
        };
      });

      const minX = Math.min(...extents.map(ext => ext.minX));
      const maxX = Math.max(...extents.map(ext => ext.maxX));
      const minY = Math.min(...extents.map(ext => ext.minY));
      const maxY = Math.max(...extents.map(ext => ext.maxY));
      const contentWidth = Math.max(100, maxX - minX);
      const contentHeight = Math.max(100, maxY - minY);
      const scaleX = width / contentWidth;
      const scaleY = height / contentHeight;
      const initialScale = Math.min(scaleX, scaleY) * 0.85;
      const clampedScale = Math.min(Math.max(initialScale, 0.25), 1.5);
      const contentCenterX = (minX + maxX) / 2;
      const contentCenterY = (minY + maxY) / 2;

      svg.call(
        zoom.transform,
        d3.zoomIdentity
          .translate(width / 2, height / 2)
          .scale(clampedScale || 1)
          .translate(-contentCenterX, -contentCenterY)
      );
    };

    svg.call(zoom);

    // Durante la animación inicial, centrar la vista en el centro
    svg.call(
      zoom.transform,
      d3.zoomIdentity
        .translate(width / 2, height / 2)
        .scale(1)
        .translate(-centerX, -centerY)
    );

    // Aplicar vista final después de que termine la animación
    setTimeout(() => {
      applyInitialView();
    }, 5500);

    const focusCluster = (skillId, scale = 2) => {
      const center = skillMeta[skillId]?.center || clusterCenters[skillId];
      if (!center) return;

      svg.transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(scale)
            .translate(-center.x, -center.y)
        );
    };

    // Agregar gradientes y filtros para cada nebulosa de skill
    skillGroups.forEach((skillId) => {
      const meta = skillMeta[skillId];
      if (!meta?.center) return;

      const gradient = defs.append('radialGradient')
        .attr('id', meta.gradientId)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', meta.nebulaRadius * 1.2);

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', `hsla(${meta.hue}, 90%, 70%, 0.4)`);

      gradient.append('stop')
        .attr('offset', '45%')
        .attr('stop-color', `hsla(${meta.hue}, 70%, 45%, 0.25)`);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', `hsla(${meta.hue}, 60%, 25%, 0)`);

      const blurFilter = defs.append('filter')
        .attr('id', meta.blurId)
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');

      blurFilter.append('feGaussianBlur')
        .attr('stdDeviation', Math.max(25, meta.nebulaRadius * 0.12));
    });

    // Crear nebulosas para cada grupo de skill con posición inicial en el centro
    const skillNebulae = g.selectAll('.skill-nebula')
      .data(skillGroups)
      .enter()
      .append('g')
      .attr('class', 'skill-nebula')
      .attr('transform', `translate(${centerX}, ${centerY})`) // Comenzar en el centro
      .style('pointer-events', 'all')
      .style('cursor', 'pointer')
      .style('pointer-events', 'all')
      .on('click', (event, d) => {
        event.stopPropagation();
        focusCluster(d);
      })
      .on('mouseenter', (event, d) => {
        event.stopPropagation();
        showSkillHoverPanel(d);
      })
      .on('mouseleave', () => {
        clearSkillHoverPanel();
      });

    skillNebulae.append('path')
      .attr('class', 'skill-nebula-base')
      .attr('d', d => skillMeta[d]?.nebulaPath || null)
      .attr('fill', d => `hsla(${skillMeta[d]?.hue ?? 0}, 80%, 45%, 0.45)`)
      .attr('opacity', 0.95) // Opacidad completa desde el inicio
      .attr('transform', 'scale(0.5)') // Comenzar mediano para mayor visibilidad
      .attr('transform-origin', '0 0')
      .attr('filter', d => skillMeta[d]?.blurId ? `url(#${skillMeta[d].blurId})` : null)
      .style('mix-blend-mode', 'screen')
      .transition()
      .delay(500)
      .duration(3500)
      .attr('transform', 'scale(1)'); // Solo crecer, sin cambiar opacidad

    skillNebulae.append('path')
      .attr('class', 'skill-nebula-cloud')
      .attr('d', d => skillMeta[d]?.nebulaPath || null)
      .attr('fill', d => `url(#${skillMeta[d]?.gradientId || 'skill-nebula-0'})`)
      .attr('opacity', 1) // Opacidad completa desde el inicio
      .attr('transform', 'scale(0.5)') // Comenzar mediano para mayor visibilidad
      .attr('transform-origin', '0 0')
      .attr('filter', d => skillMeta[d]?.blurId ? `url(#${skillMeta[d].blurId})` : null)
      .style('mix-blend-mode', 'screen')
      .transition()
      .delay(500)
      .duration(3500)
      .attr('transform', 'scale(1)'); // Solo crecer, sin cambiar opacidad

    skillNebulae.append('path')
      .attr('class', 'skill-nebula-rim')
      .attr('d', d => skillMeta[d]?.nebulaPath || null)
      .attr('fill', 'none')
      .attr('stroke', d => `hsla(${skillMeta[d]?.hue ?? 0}, 90%, 80%, 0.8)`)
      .attr('stroke-width', 3)
      .attr('transform', 'scale(0.5)')
      .attr('transform-origin', '0 0')
      .style('filter', 'url(#neon-glow)')
      .style('opacity', 0) // Sin borde visible
      .style('mix-blend-mode', 'screen')
      .transition()
      .delay(500)
      .duration(3500)
      .attr('stroke-width', 4)
      .attr('transform', 'scale(1)'); // Solo crecer, sin cambiar opacidad

    // Crear áreas clicables alrededor de la nebulosa
    const skillFocusRings = g.selectAll('.skill-focus-ring')
      .data(skillGroups)
      .enter()
      .append('circle')
      .attr('class', 'skill-focus-ring')
      .attr('cx', d => (skillMeta[d]?.center || clusterCenters[d]).x)
      .attr('cy', d => (skillMeta[d]?.center || clusterCenters[d]).y)
      .attr('r', d => (skillMeta[d]?.nebulaRadius || 200) + 10)
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 120)
      .style('opacity', 0)
      .style('mix-blend-mode', 'screen')
      .style('pointer-events', 'stroke')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        focusCluster(d);
      })
      .on('mouseenter', (event, d) => {
        event.stopPropagation();
        showSkillHoverPanel(d);
      })
      .on('mouseleave', () => {
        clearSkillHoverPanel();
      });

    // NO crear etiquetas aquí, se crearán después de las burbujas

    const hoveredBubble = { current: null };
    const showSkillHoverPanel = (skillId) => {
      if (!skillId) return;
      setSelectedAgent(null);
      const meta = skillMeta[skillId];
      const title = meta?.title || `${skillNamesMap[skillId] || `Skill ${skillId}`}`;
      const color = meta ? `hsla(${meta.hue ?? 0}, 90%, 70%, 1)` : '#ffffff';
      setHoveredSkill({ skillId, title, color });
    };

    const clearSkillHoverPanel = () => {
      setHoveredSkill(null);
    };

    const clusteredNodes = skillGroups.map((skillId) => ({
      skillId,
      nodes: bubbleData.filter(node => node.skillId === skillId)
    }));

    const skillClusters = g.selectAll('.skill-cluster')
      .data(clusteredNodes, d => d.skillId)
      .enter()
      .append('g')
      .attr('class', 'skill-cluster');

    const bubbleGroups = skillClusters.selectAll('.bubble')
      .data(d => d.nodes, d => d.id)
      .enter()
      .append('g')
      .attr('class', 'bubble')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        focusCluster(d.skillId);
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault();
        if (d.connection?.connected) {
          onContextMenu(event, d, true);
        }
      })
      .on('mouseenter', function(event, d) {
        d3.select(this).raise();
        hoveredBubble.current = d.id;
        clearSkillHoverPanel();
        setSelectedAgent(d);
      })
      .on('mouseleave', function(event, d) {
        if (hoveredBubble.current === d.id) {
          hoveredBubble.current = null;
        }
        setSelectedAgent(prev => (prev?.id === d.id ? null : prev));
      });

    // Agregar halo exterior con animación inicial
    bubbleGroups.append('circle')
      .attr('r', d => d.radius + 15)
      .attr('fill', 'rgba(0,0,0,0)')
      .attr('stroke', d => d.color)
      .attr('stroke-width', 0.5)
      .attr('opacity', 0)
      .attr('class', 'bubble-halo')
      .transition()
      .delay(3000)
      .duration(2000)
      .attr('opacity', 0.3);

    // Agregar círculo principal con animación de brillo
    bubbleGroups.append('circle')
      .attr('r', d => d.radius)
      .attr('fill', d => d.color)
      .attr('opacity', 0.3)
      .attr('filter', 'url(#neon-glow)')
      .attr('class', 'bubble-main')
      .style('box-shadow', d => `0 0 20px ${d.color}`)
      .transition()
      .delay(d => d.nodeIndex * 50) // Efecto cascada
      .duration(1500)
      .attr('opacity', 0.8);

    // Agregar anillo interior
    bubbleGroups.append('circle')
      .attr('r', d => d.radius - 5)
      .attr('fill', 'none')
      .attr('stroke', d => d.color)
      .attr('stroke-width', 1)
      .attr('opacity', 0.6)
      .attr('class', 'bubble-inner');

    // Agregar texto del agente centrado (invisible inicialmente)
    bubbleGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em') // Centrado verticalmente
      .attr('fill', 'white')
      .style('font-size', d => computeBubbleNameFontSize(d.radius) + 'px')
      .style('font-weight', 'bold')
      .style('text-shadow', '0 0 5px rgba(0,0,0,0.8)')
      .style('opacity', 0) // Invisible al inicio
      .text(d => truncateBubbleLabel(d.name || d.agent, d.radius))
      .transition()
      .delay(5500) // Aparecer al final
      .duration(1000)
      .style('opacity', 1);

    // Agregar tiempo en la parte inferior (invisible inicialmente)
    bubbleGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => `${d.radius * 0.8}px`) // 80% hacia abajo desde el centro
      .attr('fill', 'white')
      .style('font-size', d => computeBubbleTimeFontSize(d.radius) + 'px')
      .style('font-family', 'monospace')
      .style('font-weight', 'bold')
      .style('opacity', 0) // Invisible al inicio
      .style('text-shadow', '0 0 3px rgba(0,0,0,0.8)')
      .text(d => formatDuration(getStatusDurationSeconds(d, now)))
      .transition()
      .delay(5700) // Aparecer después del nombre
      .duration(800)
      .style('opacity', 0.9);

    // Animación de pulsación
    bubbleGroups.selectAll('circle')
      .style('animation', 'pulse 2s infinite');

    // Crear etiquetas de skills AL FINAL para que estén encima de todo
    const skillLabels = g.selectAll('.skill-label')
      .data(skillGroups)
      .enter()
      .append('g')
      .attr('class', 'skill-label')
      .style('cursor', 'pointer')
      .style('pointer-events', 'all') // Asegurar que reciban eventos del mouse
      .on('click', function(event, d) {
        event.stopPropagation();
        focusCluster(d);
      });

    // Agregar un círculo de fondo semitransparente para cada cúmulo
    // Agregar texto de etiqueta con fondo para mejor visibilidad
    const labelWrappers = skillLabels.append('g')
      .attr('class', 'skill-label-content')
      .attr('transform', d => {
        const center = skillMeta[d]?.center || clusterCenters[d];
        const offset = skillMeta[d]?.labelGap || 110;
        return `translate(${center.x}, ${center.y - offset})`;
      });

    // Agregar rectángulo de fondo para el texto
    labelWrappers.append('rect')
      .attr('x', d => -(skillMeta[d]?.labelWidth || 150) / 2)
      .attr('y', d => -(skillMeta[d]?.labelHeight || 30))
      .attr('width', d => skillMeta[d]?.labelWidth || 150)
      .attr('height', d => skillMeta[d]?.labelHeight || 30)
      .attr('rx', 6)
      .attr('fill', 'rgba(0, 0, 0, 0.75)')
      .attr('stroke', 'rgba(255, 255, 255, 0.25)')
      .attr('stroke-width', 0.5);

    labelWrappers.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', d => -(skillMeta[d]?.labelHeight || 30) / 2)
      .attr('fill', 'white')
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .style('text-shadow', '0 0 10px rgba(255,255,255,0.5)')
      .style('opacity', 1)
      .attr('dominant-baseline', 'middle')
      .text(d => skillMeta[d]?.title || `${skillNamesMap[d] || `Skill ${d}`} (${agentSkillsMap[d]?.length || 0})`);

    // Traer las etiquetas al frente
    skillLabels.raise();

    // Actualizar posiciones en cada tick con animación
    simulation.on('tick', () => {
      const elapsed = Date.now() - animationStartTime;
      const clusterTop = {};

      // Animar nebulosas con colisión de galaxias y movimiento continuo
      skillNebulae.each(function(skillId, index) {
        const targetCenter = skillMeta[skillId]?.center || clusterCenters[skillId];
        if (!targetCenter) return;

        let currentX, currentY;
        let scaleValue = 1;
        let rotationAngle = 0;

        if (elapsed < 5000) {
          // Animación inicial de colisión de galaxias
          const collisionProgress = elapsed / 5000;

          if (collisionProgress < 0.2) {
            // Acercamiento inicial
            const approach = collisionProgress / 0.2;
            const angle = index * Math.PI * 2 / skillGroups.length;
            const startRadius = 300;
            currentX = centerX + startRadius * Math.cos(angle) * (1 - approach);
            currentY = centerY + startRadius * Math.sin(angle) * (1 - approach);
          } else if (collisionProgress < 0.5) {
            // Mezcla y rotación en el centro
            const mixProgress = (collisionProgress - 0.2) / 0.3;
            const rotAngle = mixProgress * Math.PI * 4 + index * Math.PI / 2;
            const mixRadius = 50 * Math.sin(mixProgress * Math.PI);
            currentX = centerX + mixRadius * Math.cos(rotAngle);
            currentY = centerY + mixRadius * Math.sin(rotAngle);
          } else if (collisionProgress < 0.7) {
            // Segunda colisión con rebote
            const bounceProgress = (collisionProgress - 0.5) / 0.2;
            const angle = index * Math.PI * 2 / skillGroups.length + Math.PI / 4;
            const bounceRadius = 150 * Math.sin(bounceProgress * Math.PI);
            currentX = centerX + bounceRadius * Math.cos(angle);
            currentY = centerY + bounceRadius * Math.sin(angle);
          } else {
            // Separación final hacia posiciones objetivo
            const separateProgress = d3.easeCubicInOut((collisionProgress - 0.7) / 0.3);
            currentX = centerX + (targetCenter.x - centerX) * separateProgress;
            currentY = centerY + (targetCenter.y - centerY) * separateProgress;
          }
        } else {
          // Animación continua tipo nube después de posicionarse
          const floatTime = (elapsed - 5000) / 1000; // Tiempo en segundos desde que terminó la animación inicial

          // Movimiento flotante suave con diferentes frecuencias para cada nube
          const xFloat = Math.sin(floatTime * 0.3 + index * Math.PI / 3) * 20; // Movimiento horizontal más notable
          const yFloat = Math.cos(floatTime * 0.25 + index * Math.PI / 4) * 15; // Movimiento vertical más notable

          // Cambio suave de tamaño (respiración)
          const breathe = Math.sin(floatTime * 0.15 + index * Math.PI / 2) * 0.08 + 1; // Entre 0.92 y 1.08

          // Rotación más visible
          const rotate = Math.sin(floatTime * 0.12 + index) * 5; // Entre -5 y 5 grados

          currentX = targetCenter.x + xFloat;
          currentY = targetCenter.y + yFloat;
          scaleValue = breathe;
          rotationAngle = rotate;
        }

        // Aplicar todas las transformaciones
        const transformString = elapsed >= 5000
          ? `translate(${currentX}, ${currentY}) scale(${scaleValue}) rotate(${rotationAngle})`
          : `translate(${currentX}, ${currentY})`;

        d3.select(this).attr('transform', transformString);

        // Animar los paths internos de la nebulosa para simular cambio de forma
        if (elapsed >= 5000) {
          const morphTime = (elapsed - 5000) / 1000;
          const morphScale = 1 + Math.sin(morphTime * 0.18 + index * 2) * 0.06; // Cambio más notable

          // Cambio más visible de opacidad
          const opacityBase = 0.85 + Math.sin(morphTime * 0.15 + index) * 0.15; // Entre 0.7 y 1
          const opacityCloud = 0.9 + Math.sin(morphTime * 0.2 + index * 1.5) * 0.2; // Entre 0.7 y 1.1 (clamped)

          d3.select(this).select('.skill-nebula-base')
            .attr('transform', `scale(${morphScale})`)
            .attr('transform-origin', '0 0')
            .attr('opacity', opacityBase);

          d3.select(this).select('.skill-nebula-cloud')
            .attr('transform', `scale(${1.1 / morphScale})`) // Escala inversa más pronunciada
            .attr('transform-origin', '0 0')
            .attr('opacity', Math.min(1, Math.max(0.7, opacityCloud)));

          // Animar el blur también para simular cambio de densidad
          const blurAmount = 20 + Math.sin(morphTime * 0.25 + index * 0.5) * 10; // Entre 10 y 30
          d3.select(this).select('.skill-nebula-base')
            .attr('filter', `url(#${skillMeta[skillId]?.blurId || 'skill-blur-0'})`);
        }
      });

      // Fase 1: Rotación MÁS RÁPIDA (0-3 segundos)
      if (animationPhase === 'rotating' && elapsed < 3000) {
        // Reducir velocidad gradualmente
        rotationSpeed = 0.35 * (1 - elapsed / 3000) + 0.05; // Más rápido

        bubbleData.forEach(d => {
          const angle = d.initialAngle + rotationSpeed * elapsed / 100;
          const radius = Math.min(150, totalBubbles * 3);
          d.x = centerX + radius * Math.cos(angle);
          d.y = centerY + radius * Math.sin(angle);
        });
      }
      // Fase 2: Expansión gradual (3-5 segundos)
      else if (animationPhase === 'rotating' || animationPhase === 'expanding') {
        if (animationPhase === 'rotating') {
          animationPhase = 'expanding';
          // Activar fuerzas de atracción gradualmente
          simulation
            .force('x', d3.forceX(d => d.clusterX).strength(0))
            .force('y', d3.forceY(d => d.clusterY).strength(0))
            .force('charge', d3.forceManyBody().strength(d => -Math.pow(d.radius, 1.2)));
        }

        // Interpolar hacia las posiciones del cluster
        const expandProgress = Math.min(1, (elapsed - 3000) / 2000);
        const easeProgress = d3.easeCubicInOut(expandProgress);

        bubbleData.forEach(d => {
          const currentAngle = d.initialAngle + 0.02 * elapsed / 100;
          const rotRadius = Math.min(150, totalBubbles * 3) * (1 - easeProgress);
          const rotX = centerX + rotRadius * Math.cos(currentAngle);
          const rotY = centerY + rotRadius * Math.sin(currentAngle);

          // Interpolar entre posición rotando y posición del cluster
          d.x = rotX + (d.clusterX - rotX) * easeProgress;
          d.y = rotY + (d.clusterY - rotY) * easeProgress;
        });

        // Aumentar gradualmente la fuerza
        if (simulation.force('x')) {
          simulation.force('x').strength(0.3 * expandProgress);
        }
        if (simulation.force('y')) {
          simulation.force('y').strength(0.3 * expandProgress);
        }

        if (elapsed > 5000) {
          animationPhase = 'settling';
        }
      }
      // Fase 3: Asentamiento final (5+ segundos)
      else if (animationPhase === 'settling') {
        // Activar todas las fuerzas finales
        if (!simulation.force('cluster')) {
          simulation
            .force('x', d3.forceX(d => d.clusterX).strength(0.7))
            .force('y', d3.forceY(d => d.clusterY).strength(0.7))
            .force('charge', d3.forceManyBody().strength(d => -Math.pow(d.radius, 1.5)))
            .force('cluster', forceCluster())
            .alphaDecay(0.02); // Activar decaimiento normal
        }
      }

      // Actualizar posiciones visuales
      bubbleGroups.each(function(d) {
        const isHovered = hoveredBubble.current === d.id;
        const scale = isHovered ? 1.1 : 1;
        d3.select(this).attr('transform', `translate(${d.x},${d.y}) scale(${scale})`);
        const topEdge = d.y - d.radius * scale;
        if (clusterTop[d.skillId] === undefined || topEdge < clusterTop[d.skillId]) {
          clusterTop[d.skillId] = topEdge;
        }
      });

      labelWrappers.each(function(skillId) {
        const meta = skillMeta[skillId] || {};
        const baseCenter = meta.center || clusterCenters[skillId] || { x: 0, y: 0 };
        const fallbackTop = baseCenter.y - (meta.nebulaRadius || 200);
        const top = typeof clusterTop[skillId] === 'number' ? clusterTop[skillId] : fallbackTop;
        const targetY = top - (meta.labelGap || 30);
        d3.select(this).attr('transform', `translate(${baseCenter.x}, ${targetY})`);
      });
    });

    // Guardar referencias
    simulationRef.current = simulation;
    nodesRef.current = bubbleGroups;

    setInitialized(true);

    // Cleanup
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [agents, dimensions, onContextMenu]);

  // Actualizar solo el tiempo (cada segundo)
  useEffect(() => {
    if (!initialized || !nodesRef.current) return;

    // Actualizar solo el texto del tiempo para cada burbuja
    nodesRef.current.each(function(d) {
      const group = d3.select(this);
      // Seleccionar el último texto (que es el tiempo - segundo y último texto)
      group.select('text:last-of-type')
        .text(formatDuration(getStatusDurationSeconds(d, now)));
    });
  }, [now, initialized]);

  // Actualizar cuando cambian los agentes
  useEffect(() => {
    if (!initialized || !nodesRef.current || !simulationRef.current) return;

    const connectedAgentsList = agents.filter(agent => agent.connection?.connected);
    const { agentSkillsMap: updatedSkillMap, skillNamesMap: updatedSkillNames } = buildSkillGrouping(connectedAgentsList);
    skillAgentsRef.current = updatedSkillMap;
    skillNamesRef.current = updatedSkillNames;

    const agentMap = agents.reduce((acc, agent) => {
      acc[agent.agent] = agent;
      return acc;
    }, {});

    simulationRef.current.nodes().forEach(node => {
      const currentAgent = agentMap[node.agent];
      if (currentAgent) {
        Object.assign(node, currentAgent);
        const newRadius = computeBubbleRadius(currentAgent, now);
        node.radius = newRadius;
        node.color = getAgentStatusNeonColor(currentAgent.status);
        node.statusLabel = getAgentStatusLabel(currentAgent.status);
      }
    });

    nodesRef.current.each(function(d) {
      const group = d3.select(this);
      const currentAgent = agentMap[d.agent];

      if (currentAgent) {
        Object.assign(d, currentAgent);
        const newColor = getAgentStatusNeonColor(currentAgent.status);
        const newRadius = computeBubbleRadius(currentAgent, now);
        const durationSeconds = getStatusDurationSeconds(currentAgent, now);
        const durationText = formatDuration(durationSeconds);

        group.select('.bubble-halo')
          .transition()
          .duration(400)
          .attr('r', newRadius + 15)
          .attr('stroke', newColor);

        group.select('.bubble-main')
          .transition()
          .duration(400)
          .attr('r', newRadius)
          .attr('fill', newColor);

        group.select('.bubble-inner')
          .transition()
          .duration(400)
          .attr('r', Math.max(newRadius - 5, 1))
          .attr('stroke', newColor);

        group.select('text:first-of-type')
          .style('font-size', `${computeBubbleNameFontSize(newRadius)}px`)
          .text(truncateBubbleLabel(currentAgent.name || currentAgent.agent, newRadius));

        group.select('text:last-of-type')
          .attr('dy', `${newRadius * 0.8}px`)
          .style('font-size', `${computeBubbleTimeFontSize(newRadius)}px`)
          .text(durationText);
      }
    });

    simulationRef.current.alpha(0.2).restart();
  }, [agents, initialized, now]); // Cuando cambian los agentes

  const hoveredSkillDetails = useMemo(() => {
    if (!hoveredSkill?.skillId) {
      return null;
    }
    const skillId = hoveredSkill.skillId;
    const meta = hoveredSkill;
    const title = meta.title || skillMetaRef.current[skillId]?.title || `${skillNamesRef.current[skillId] || `Skill ${skillId}`}`;
    const color = meta.color || `hsla(${skillMetaRef.current[skillId]?.hue ?? 0}, 90%, 70%, 1)`;
    const agentsInSkill = skillAgentsRef.current[skillId] || [];
    if (!agentsInSkill.length) {
      return null;
    }

    const formattedAgents = agentsInSkill
      .map(agent => {
        const durationSeconds = getStatusDurationSeconds(agent, now);
        return {
          id: agent.agent,
          name: agent.name || agent.agent,
          statusLabel: getAgentStatusLabel(agent.status),
          color: getAgentStatusNeonColor(agent.status),
          duration: formatDuration(durationSeconds),
          durationSeconds
        };
      })
      .sort((a, b) => b.durationSeconds - a.durationSeconds);

    return {
      title,
      color,
      agents: formattedAgents
    };
  }, [hoveredSkill, now]);

  useEffect(() => {
    if (hoveredSkill?.skillId && !skillAgentsRef.current[hoveredSkill.skillId]) {
      setHoveredSkill(null);
    }
  }, [hoveredSkill, agents]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: immersive ? '100vw' : '100%',
        height: immersive ? 'calc(100vh - 64px)' : '100vh',
        minHeight: immersive ? 'calc(100vh - 64px)' : 600,
        position: immersive ? 'fixed' : 'relative',
        top: immersive ? 64 : 'auto',
        left: immersive ? 0 : 'auto',
        borderRadius: immersive ? 0 : 2,
        zIndex: immersive ? 5 : 'auto',
        overflow: 'hidden',
        background: 'radial-gradient(ellipse at center, #0a0e27 0%, #000000 100%)',
        '& svg': {
          width: '100%',
          height: '100%'
        },
        '& @keyframes twinkle': {
          '0%, 100%': { opacity: 0.2 },
          '50%': { opacity: 1 }
        },
        '& @keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.05)' }
        },
        '& @keyframes cloudFloat': {
          '0%, 100%': {
            filter: 'blur(25px)',
            opacity: 0.95
          },
          '50%': {
            filter: 'blur(30px)',
            opacity: 1
          }
        },
        '& @keyframes cloudMorph': {
          '0%, 100%': {
            transform: 'scale(1) rotate(0deg)'
          },
          '33%': {
            transform: 'scale(1.02) rotate(1deg)'
          },
          '66%': {
            transform: 'scale(0.98) rotate(-1deg)'
          }
        }
      }}
    >
      <svg ref={svgRef} />

      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          display: 'flex',
          gap: 1
        }}
      >
        {immersive && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={onExitImmersive}
            sx={{
              px: 1.4,
              py: 0.35,
              fontSize: '0.68rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
            }}
          >
            Volver a tabla
          </Button>
        )}
      </Box>

      {/* Panel de información del agente seleccionado o del skill */}
      {selectedAgent ? (
        <Paper
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            p: 2,
            minWidth: 250,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: selectedAgent.color,
            boxShadow: `0 0 20px ${selectedAgent.color}`,
            color: 'white'
          }}
        >
          <Stack spacing={1}>
            <Typography variant="h6" sx={{ color: selectedAgent.color }}>
              {selectedAgent.name || selectedAgent.agent}
            </Typography>
            <Typography variant="body2">
              ID: {selectedAgent.agent}
            </Typography>
            <Typography variant="body2">
              Estado: {selectedAgent.statusLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '1.2rem' }}>
              {formatDuration(getStatusDurationSeconds(selectedAgent, now))}
            </Typography>
          </Stack>
        </Paper>
      ) : hoveredSkillDetails ? (
        <Paper
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            p: 2,
            minWidth: 260,
            maxWidth: 360,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: hoveredSkillDetails.color,
            boxShadow: `0 0 25px ${hoveredSkillDetails.color}`,
            color: 'white',
            maxHeight: 360,
            overflow: 'hidden'
          }}
        >
          <Stack spacing={1}>
            <Typography variant="h6" sx={{ color: hoveredSkillDetails.color }}>
              {hoveredSkillDetails.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {hoveredSkillDetails.agents.length} agentes conectados
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
              {hoveredSkillDetails.agents.map(agent => (
                <Stack
                  key={agent.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    p: 0.75,
                    borderRadius: 1,
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {agent.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {agent.statusLabel}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      color: agent.color || 'white',
                      fontWeight: 600
                    }}
                  >
                    {agent.duration}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {/* Controles de navegación */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: 1
        }}
      >
        <Typography variant="caption" sx={{ color: 'white', px: 2, display: 'flex', alignItems: 'center' }}>
          Scroll para zoom | Arrastra para mover | Pasa el mouse para ver detalles
        </Typography>
      </Box>
    </Box>
  );
};

const SupervisorAgentsPage = () => {
  const api = useApi();
  const auth = useAuth();
  const { showError, showSuccess, showInfo, showWarning } = useNotification();
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({
    agent: '',
    campaignId: '',
    skillId: '',
    status: '',
    supervisorId: '',
    managerId: '',
    hideDisconnected: true,
    onlyAgents: true
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const statusOptions = useMemo(
    () => AGENT_STATUS_ORDER.filter(status => status !== 'BUSY'),
    []
  );
  const manualStatusOptions = useMemo(
    () => statusOptions.filter(status => !SYSTEM_MANAGED_STATUSES.includes(status)),
    [statusOptions]
  );
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [userSettingsKey, setUserSettingsKey] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const savePreferencesTimeout = useRef(null);
  const LOCAL_STORAGE_KEY = useMemo(
    () => (userSettingsKey ? `cti_supervisor_settings_${userSettingsKey}` : null),
    [userSettingsKey]
  );
  const [menuAgent, setMenuAgent] = useState(null);
  const [contextMenuState, setContextMenuState] = useState(null);
  const [screenViewerOpen, setScreenViewerOpen] = useState(false);
  const [screenViewerAgent, setScreenViewerAgent] = useState(null);
  const [statusMenuPosition, setStatusMenuPosition] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedAgentForDetails, setSelectedAgentForDetails] = useState(null);
  const [tableZoom, setTableZoom] = useState(100);
  const [zoomAnchorEl, setZoomAnchorEl] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [quickPanelAgent, setQuickPanelAgent] = useState(null);
  const { hangup: softphoneHangup, setMonitoringSession, clearMonitoringSession } = useSoftphone();
  const { canAction } = usePermissions();
  // Cache local de acumulados de disponibilidad por día (para sobrevivir refrescos)
  const availableCacheRef = useRef({});
  const todayKeyRef = useRef(new Date().toISOString().slice(0, 10));

  const enhanceAgentRecord = useCallback((agent, referenceMs = Date.now()) => {
    const hasDuration = typeof agent?.statusDurationSeconds === 'number';
    let baseTimestamp = null;

    if (agent?.lastStatusChange) {
      baseTimestamp = new Date(agent.lastStatusChange).getTime();
    } else if (typeof agent?.lastStatusChangeMs === 'number') {
      baseTimestamp = agent.lastStatusChangeMs;
    } else if (hasDuration) {
      baseTimestamp = referenceMs - agent.statusDurationSeconds * 1000;
    }

    const connectionSecondsBase = Number(agent?.connectionSecondsBase ?? 0);
    const currentOnlineSinceMs = typeof agent?.currentOnlineSinceMs === 'number'
      ? agent.currentOnlineSinceMs
      : agent?.currentOnlineSince
        ? new Date(agent.currentOnlineSince).getTime()
        : null;

    let availableSecondsToday = typeof agent?.availableSecondsToday === 'number'
      ? Math.max(0, Math.floor(agent.availableSecondsToday))
      : null;

    // Fallback: si no viene acumulado, usar la duración reportada del estado (backend statusDurationSeconds)
    if (agent?.status === 'AVAILABLE' && (availableSecondsToday === null || Number.isNaN(availableSecondsToday))) {
      const fallbackBase = hasDuration ? Math.max(0, Math.floor(agent.statusDurationSeconds)) : 0;
      availableSecondsToday = fallbackBase;
      console.warn('[SUPERVISOR][LOAD] AVAILABLE sin acumulado, usando fallback', {
        agent: agent?.agent,
        fallbackBase,
        statusDurationSeconds: agent?.statusDurationSeconds
      });
    }

    if (agent?.status === 'AVAILABLE') {
      console.log('[SUPERVISOR][LOAD] enhanceAgentRecord AVAILABLE', {
        agent: agent?.agent,
        statusDurationSeconds: agent?.statusDurationSeconds,
        availableSecondsToday,
        referenceMs
      });
    }

    return {
      ...agent,
      snapshotMs: referenceMs,
      lastStatusChange: baseTimestamp ? new Date(baseTimestamp).toISOString() : null,
      lastStatusChangeMs: baseTimestamp,
      statusDurationSecondsBase: agent?.statusDurationSeconds ?? 0,
      availableSecondsToday,
      connectionSecondsBase,
      currentOnlineSinceMs,
      stateChangeCount: agent?.stateChangeCount ?? 0,
      callCountToday: agent?.callCountToday ?? 0,
      firstConnectionAt: agent?.firstConnectionAt ? new Date(agent.firstConnectionAt).toISOString() : null,
      lastDisconnectAt: agent?.lastDisconnectAt ? new Date(agent.lastDisconnectAt).toISOString() : null
    };
  }, []);

  const isColumnVisible = useCallback(
    (column) => visibleColumns.includes(column),
    [visibleColumns]
  );

  const applyUserPreferences = useCallback((prefs = {}) => {
    if (!prefs || typeof prefs !== 'object') return;

    if (prefs.filters && typeof prefs.filters === 'object') {
      setFilters(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(prefs.filters).map(([key, value]) => [key, value ?? ''])
        )
      }));
    }

    if (prefs.columnFilters && typeof prefs.columnFilters === 'object' && !Array.isArray(prefs.columnFilters)) {
    const sanitized = {};
    Object.entries(prefs.columnFilters).forEach(([key, value]) => {
      if (COLUMN_CONFIG[key]) {
        sanitized[key] = typeof value === 'string' ? value : String(value ?? '');
      }
      });
      setColumnFilters(sanitized);
      setActiveFilterColumn(null);
    }

    if (Array.isArray(prefs.visibleColumns) && prefs.visibleColumns.length > 0) {
      const valid = prefs.visibleColumns.filter(value => COLUMN_CONFIG[value]);
      if (valid.length > 0) {
        setVisibleColumns(valid);
      }
    }

    if (prefs.sortConfig && typeof prefs.sortConfig === 'object') {
      const column = COLUMN_CONFIG[prefs.sortConfig.column] ? prefs.sortConfig.column : null;
      const direction = prefs.sortConfig.direction === 'desc' ? 'desc' : 'asc';
      setSortConfig({ column, direction });
    }
  }, []);

  const handleColumnsChange = (event) => {
    const value = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;
    if (!value.length) {
      return;
    }
    setVisibleColumns(value);
  };

  const handleSortChange = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  useEffect(() => {
    const rawKey =
      auth?.user?.agent ||
      auth?.user?.username ||
      auth?.user?.id ||
      null;

    setUserSettingsKey(rawKey ? String(rawKey).trim() : null);
  }, [auth?.user]);

  useEffect(() => {
    let isCancelled = false;
    setSettingsLoaded(false);

    if (!userSettingsKey) {
      setSettingsLoaded(true);
      return;
    }

    const localPrefs = LOCAL_STORAGE_KEY ? localStorage.getItem(LOCAL_STORAGE_KEY) : null;
    if (localPrefs) {
      try {
        const parsed = JSON.parse(localPrefs);
        applyUserPreferences(parsed);
      } catch (error) {
        console.warn('[SUPERVISOR] Failed to parse local settings:', error);
      }
    }

    setSettingsLoaded(true);

    return () => {
      isCancelled = true;
    };
  }, [userSettingsKey, applyUserPreferences, LOCAL_STORAGE_KEY]);

  useEffect(() => {
    return () => {
      if (savePreferencesTimeout.current) {
        clearTimeout(savePreferencesTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!userSettingsKey || !settingsLoaded) {
      return;
    }

    const payload = {
      filters,
      columnFilters,
      visibleColumns,
      sortConfig
    };

    if (savePreferencesTimeout.current) {
      clearTimeout(savePreferencesTimeout.current);
    }

    savePreferencesTimeout.current = setTimeout(() => {
      if (LOCAL_STORAGE_KEY) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      }
    }, 400);

    return () => {
      if (savePreferencesTimeout.current) {
        clearTimeout(savePreferencesTimeout.current);
      }
    };
  }, [filters, columnFilters, visibleColumns, sortConfig, userSettingsKey, settingsLoaded, LOCAL_STORAGE_KEY]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);


  const loadReferenceData = useCallback(async () => {
    try {
      const [skillsResponse, campaignsResponse] = await Promise.all([
        api.skills.getAll(),
        api.campaigns.getAll()
      ]);

      if (skillsResponse?.success) {
        setSkills(skillsResponse.data?.skills || []);
      }

      if (campaignsResponse?.success) {
        setCampaigns(campaignsResponse.data?.campaigns || []);
      }
    } catch (error) {
      console.error('[SUPERVISOR] Error cargando catálogos', error);
      showError('No se pudieron cargar las listas de skills/Campañas');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      // preparar cache diario
      const todayKey = new Date().toISOString().slice(0, 10);
      todayKeyRef.current = todayKey;
      const cached = sessionStorage.getItem(`cti_supervisor_available_${todayKey}`);
      if (cached) {
        try {
          availableCacheRef.current = JSON.parse(cached) || {};
        } catch {
          availableCacheRef.current = {};
        }
      } else {
        availableCacheRef.current = {};
      }

      const response = await api.agents.getStatusBoard();
      if (!response?.success) {
        throw new Error(response?.message || 'Respuesta inválida');
      }

      const rawAgents = response.data?.agents || [];
      const snapshotTimestamp = response.data?.generatedAt
        ? new Date(response.data.generatedAt).getTime()
        : Date.now();

      const availableSample = rawAgents
        .filter(a => a.status === 'AVAILABLE')
        .slice(0, 3)
        .map(a => ({
          agent: a.agent,
          statusDurationSeconds: a.statusDurationSeconds,
          availableSecondsToday: a.availableSecondsToday,
          lastStatusChange: a.lastStatusChange
        }));
      console.log('[SUPERVISOR][LOAD] status-board AVAILABLE sample', availableSample);

      const enriched = rawAgents.map(agent => {
        const enrichedAgent = enhanceAgentRecord(agent, snapshotTimestamp);

        // Aplicar cache local solo si el backend no envía availableSecondsToday
        if (enrichedAgent.status === 'AVAILABLE') {
          const cache = availableCacheRef.current || {};
          const hasBackendAvailable = typeof enrichedAgent.availableSecondsToday === 'number';
          if (hasBackendAvailable) {
            cache[enrichedAgent.agent] = enrichedAgent.availableSecondsToday;
          } else {
            const prev = Number(cache[enrichedAgent.agent] || 0);
            const merged = Math.max(prev, 0);
            cache[enrichedAgent.agent] = merged;
            enrichedAgent.availableSecondsToday = merged;
          }
        }

        return enrichedAgent;
      });

      // Persistir cache actualizado
      try {
        sessionStorage.setItem(`cti_supervisor_available_${todayKeyRef.current}`, JSON.stringify(availableCacheRef.current));
      } catch {}

      setAgents(enriched);
      setLastUpdated(response.data?.generatedAt || new Date().toISOString());
    } catch (error) {
      console.error('[SUPERVISOR] Error cargando estado de agentes', error);
      showError('No se pudo cargar el tablero de supervisión');
    } finally {
      setLoading(false);
    }
  }, [api.agents, showError, enhanceAgentRecord]);

  useEffect(() => {
    loadReferenceData();
    loadAgents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!api?.sse) return undefined;

    const handleStatusEvent = (event) => {
      try {
        const data = JSON.parse(event.data);
        const targetId = data.agentId || data.agent?.agent;
        if (!targetId) return;

        const rawEventTime = data.lastStatusChange || data.timestamp || null;
        const eventTimeMs = rawEventTime ? new Date(rawEventTime).getTime() : Date.now();
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startOfTodayMs = startOfToday.getTime();

        setAgents((prev) => {
          const ignoredStateChangeStatuses = new Set(['AVAILABLE', 'BUSY', 'INCALL', 'ACW']);
          if (!prev.some(agent => agent.agent === targetId)) {
            return prev;
          }

          return prev.map(agent => {
            if (agent.agent !== targetId) {
              return agent;
            }

            const nowMs = Date.now();
            const nextStatus = data.status || agent.status;
            const previousStatus = agent.status;
            const currentDuration = getStatusDurationSeconds(agent, nowMs);
            let baseAvailable = agent.status === 'AVAILABLE'
              ? currentDuration
              : (agent.availableSecondsToday || 0);
            if (nextStatus === 'AVAILABLE' && HARD_RESET_STATUSES.has(previousStatus)) {
              baseAvailable = 0;
            }
            const isTodayEvent = eventTimeMs >= startOfTodayMs;
            const elapsedOnline = agent.currentOnlineSinceMs
              ? Math.max(0, Math.floor((nowMs - agent.currentOnlineSinceMs) / 1000))
              : 0;
            const finalizedBase = (agent.connectionSecondsBase || 0) + elapsedOnline;
            const nextConnectionSecondsBase = finalizedBase;
            const nextCurrentOnlineSinceMs = isOnlineStatus(nextStatus) ? nowMs : null;
            const shouldCountChange = isTodayEvent && !ignoredStateChangeStatuses.has(nextStatus);
            const nextStateChangeCount = shouldCountChange
              ? (agent.stateChangeCount || 0) + 1
              : agent.stateChangeCount || 0;
            const nextFirstConnectionAt = agent.firstConnectionAt
              || (isTodayEvent && isOnlineStatus(nextStatus)
                ? new Date(eventTimeMs).toISOString()
                : null);
            const nextLastDisconnectAt = (!isOnlineStatus(nextStatus) && isTodayEvent)
              ? new Date(eventTimeMs).toISOString()
              : agent.lastDisconnectAt;

            const nextAgent = {
              ...agent,
              status: nextStatus,
              pauseCode: data.pauseCode ?? agent.pauseCode,
              callDirection: data.callDirection || (nextStatus === previousStatus ? agent.callDirection : null),
              lastStatusChange: rawEventTime ? new Date(eventTimeMs).toISOString() : new Date().toISOString(),
              statusDurationSeconds: nextStatus === 'AVAILABLE' ? baseAvailable : 0,
              stateChangeCount: nextStateChangeCount,
              firstConnectionAt: nextFirstConnectionAt,
              lastDisconnectAt: nextLastDisconnectAt,
              connectionSecondsBase: nextConnectionSecondsBase,
              currentOnlineSinceMs: nextCurrentOnlineSinceMs,
              availableSecondsToday: baseAvailable,
              connection: {
                ...(agent.connection || {}),
                connected: data.isConnected ?? agent.connection?.connected ?? false
              }
            };

            console.debug('[SUPERVISOR][SSE] agent-status', {
              targetId,
              nextStatus,
              baseAvailable,
              currentDuration,
              availableSecondsToday_prev: agent.availableSecondsToday,
              statusDurationSeconds_prev: agent.statusDurationSeconds,
              statusDurationSeconds_next: nextAgent.statusDurationSeconds,
              lastStatusChange_prev: agent.lastStatusChange,
              lastStatusChange_next: nextAgent.lastStatusChange,
              eventTimeMs,
              nowMs
            });

            return enhanceAgentRecord(nextAgent, nowMs);
          });
        });
      } catch (error) {
        console.error('[SUPERVISOR] Error procesando SSE agent-status', error);
      }
    };

    api.sse.addEventListener('agent-status', handleStatusEvent);
    return () => {
      api.sse.removeEventListener('agent-status');
    };
  }, [api, enhanceAgentRecord]);

  // Listen for call direction updates from ARI (arrives after status change)
  useEffect(() => {
    if (!api?.sse) return;
    const handleCallDirection = (event) => {
      try {
        const data = typeof event === 'string' ? JSON.parse(event) : (event?.data ? JSON.parse(event.data) : event);
        const { extension, callDirection } = data;
        if (!extension || !callDirection) return;
        setAgents(prev => prev.map(agent => {
          if (agent.extension !== extension) return agent;
          return { ...agent, callDirection };
        }));
      } catch (e) {}
    };
    api.sse.addEventListener('agent-call-direction', handleCallDirection);
    return () => {
      api.sse.removeEventListener('agent-call-direction');
    };
  }, [api]);

  const campaignLookup = useMemo(() => {
    const map = new Map();
    campaigns.forEach(campaign => {
      map.set(String(campaign.idcampaign), campaign.descripcion || `Campaña ${campaign.idcampaign}`);
    });
    return map;
  }, [campaigns]);

  const agentNameLookup = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => {
      if (agent.agent) {
        map.set(String(agent.agent), agent.name || agent.agent);
      }
    });
    return map;
  }, [agents]);

  const supervisorOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    agents.forEach((agent) => {
      const id = agent.supervisor_id || agent.supervisor;
      const value = id ? String(id).trim() : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ id: value, name: agentNameLookup.get(value) || value });
    });
    return options;
  }, [agents, agentNameLookup]);

  const managerOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    agents.forEach((agent) => {
      const id = agent.manager_id || agent.manager;
      const value = id ? String(id).trim() : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ id: value, name: agentNameLookup.get(value) || value });
    });
    return options;
  }, [agents, agentNameLookup]);

  const baseFilteredAgents = useMemo(() => {
    return agents.filter(agent => {
      if (filters.agent) {
        const query = filters.agent.trim().toLowerCase();
        const matchesAgent =
          (agent.agent || '').toLowerCase().includes(query) ||
          (agent.name || '').toLowerCase().includes(query) ||
          (agent.extension || '').toLowerCase().includes(query);
        if (!matchesAgent) return false;
      }

      if (filters.campaignId) {
        const matchesCampaign = (agent.campaigns || []).some(
          id => String(id) === String(filters.campaignId)
        );
        if (!matchesCampaign) return false;
      }

      if (filters.skillId) {
        const matchesSkill = (agent.skills || []).some(
          skill => String(skill.id) === String(filters.skillId)
        );
        if (!matchesSkill) return false;
      }

      if (filters.status) {
        if ((agent.status || '') !== filters.status) {
          return false;
        }
      }

      if (filters.supervisorId) {
        const sup = agent.supervisor_id || agent.supervisor || '';
        if (String(sup).trim() !== filters.supervisorId) {
          return false;
        }
      }

      if (filters.managerId) {
        const mgr = agent.manager_id || agent.manager || '';
        if (String(mgr).trim() !== filters.managerId) {
          return false;
        }
      }

      if (filters.hideDisconnected) {
        if (!agent.connection?.connected) {
          return false;
        }
      }

      if (filters.onlyAgents) {
        if (!isAgentRole(agent)) {
          return false;
        }
      }

      return true;
    });
  }, [agents, filters]);

  const getColumnFilterString = useCallback((agent, column) => {
    switch (column) {
      case 'agentId':
        return agent.agent || '';
      case 'name':
        return agent.name || '';
      case 'extension':
        return agent.extension || '';
      case 'status':
        return getAgentStatusLabel(agent.status) || agent.status || '';
      case 'skills':
        return (agent.skills || []).map(skill => `${skill.name} ${skill.def}`).join(' ');
      case 'campaigns':
        return (agent.campaigns || []).map(id => campaignLookup.get(String(id)) || String(id)).join(' ');
      default:
        return '';
    }
  }, [campaignLookup]);

  const columnFilteredAgents = useMemo(() => {
    const activeFilters = Object.entries(columnFilters)
      .filter(([column, value]) => Boolean(COLUMN_CONFIG[column]?.filterable) && value?.trim());

    if (activeFilters.length === 0) {
      return baseFilteredAgents;
    }

    return baseFilteredAgents.filter(agent =>
      activeFilters.every(([column, value]) => {
        const normalized = value.trim().toLowerCase();
        const candidate = getColumnFilterString(agent, column).toLowerCase();
        return candidate.includes(normalized);
      })
    );
  }, [baseFilteredAgents, columnFilters, getColumnFilterString]);

  const sortedAgents = useMemo(() => {
    if (!sortConfig.column) {
      return columnFilteredAgents;
    }

    const columnType = COLUMN_CONFIG[sortConfig.column]?.type || 'string';
    const data = [...columnFilteredAgents];

    const getSortValue = (agent) => {
      switch (sortConfig.column) {
        case 'agentId':
          return agent.agent || '';
        case 'name':
          return agent.name || '';
        case 'extension':
          return agent.extension || '';
        case 'status':
          return agent.status || '';
        case 'stateChanges':
          return agent.stateChangeCount || 0;
        case 'callCount':
          return agent.callCountToday || 0;
        case 'time':
          return getStatusDurationSeconds(agent, now);
        case 'connectionTime':
          return getConnectionSeconds(agent, now);
        case 'firstConnection':
          return agent.firstConnectionAt ? new Date(agent.firstConnectionAt).getTime() : 0;
        case 'lastDisconnect':
          return agent.lastDisconnectAt ? new Date(agent.lastDisconnectAt).getTime() : 0;
        case 'skills':
          return (agent.skills || []).length;
        case 'campaigns':
          return (agent.campaigns || []).length;
        default:
          return '';
      }
    };

    data.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);

      if (columnType === 'number' || columnType === 'datetime') {
        const diff = (Number(valueA) || 0) - (Number(valueB) || 0);
        if (diff === 0) return 0;
        return sortConfig.direction === 'asc' ? diff : -diff;
      }

      const stringA = (valueA || '').toString().toLowerCase();
      const stringB = (valueB || '').toString().toLowerCase();
      if (stringA === stringB) return 0;
      if (stringA > stringB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return sortConfig.direction === 'asc' ? -1 : 1;
    });

    return data;
  }, [columnFilteredAgents, sortConfig, now]);

  const statusSummary = useMemo(() => {
    return columnFilteredAgents.reduce(
      (acc, agent) => {
        acc.total += 1;
        acc.byStatus[agent.status] = (acc.byStatus[agent.status] || 0) + 1;
        acc.connected += agent.connection?.connected ? 1 : 0;

        // Contadores específicos por estado
        acc.available += agent.status === 'AVAILABLE' ? 1 : 0;
        acc.busy += agent.status === 'BUSY' ? 1 : 0;
        acc.incall += agent.status === 'INCALL' ? 1 : 0;
        acc.acw += agent.status === 'ACW' ? 1 : 0;
        acc.precall += agent.status === 'PRECALL' ? 1 : 0;
        acc.pause += agent.status === 'PAUSE' ? 1 : 0;
        acc.break += agent.status === 'BREAK' ? 1 : 0;
        acc.bathroom += agent.status === 'BATHROOM' ? 1 : 0;
        acc.lunch += agent.status === 'LUNCH' ? 1 : 0;
        acc.rest += agent.status === 'REST' ? 1 : 0;
        acc.training += agent.status === 'TRAINING' ? 1 : 0;
        acc.absent += agent.status === 'ABSENT' ? 1 : 0;
        acc.notready += agent.status === 'NOTREADY' ? 1 : 0;
        acc.offline += agent.status === 'OFFLINE' ? 1 : 0;
        acc.no_network += agent.status === 'NO_NETWORK' ? 1 : 0;

        // Agrupadores
        acc.inCallGroup += ['BUSY', 'INCALL', 'ACW', 'PRECALL'].includes(agent.status) ? 1 : 0;
        acc.paused += ['PAUSE', 'BREAK', 'BATHROOM', 'LUNCH', 'REST', 'TRAINING'].includes(agent.status) ? 1 : 0;

        acc.totalCalls += agent.callCountToday || 0;
        acc.avgStateChanges += agent.stateChangeCount || 0;
        return acc;
      },
      {
        total: 0,
        connected: 0,
        available: 0,
        busy: 0,
        incall: 0,
        acw: 0,
        precall: 0,
        pause: 0,
        break: 0,
        bathroom: 0,
        lunch: 0,
        rest: 0,
        training: 0,
        absent: 0,
        notready: 0,
        offline: 0,
        no_network: 0,
        inCallGroup: 0,
        paused: 0,
        totalCalls: 0,
        avgStateChanges: 0,
        byStatus: {}
      }
    );
  }, [columnFilteredAgents]);

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      agent: '',
      campaignId: '',
      skillId: '',
      status: '',
      supervisorId: '',
      managerId: '',
      hideDisconnected: true,
      onlyAgents: true
    });
    setColumnFilters({});
    setActiveFilterColumn(null);
  };

  const handleZoomIn = () => {
    setTableZoom(prev => Math.min(prev + 10, 150));
  };

  const handleZoomOut = () => {
    setTableZoom(prev => Math.max(prev - 10, 50));
  };

  const handleZoomReset = () => {
    setTableZoom(100);
  };

  const handleAgentClick = useCallback((agent) => {
    setQuickPanelAgent(agent);
  }, []);

  const handleAgentContextMenu = useCallback((event, agent, connected) => {
    event.preventDefault();
    if (!connected) {
      return;
    }
    setStatusMenuPosition(null);
    setMenuAgent(agent);
    setContextMenuState({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuState(null);
    setStatusMenuPosition(null);
    setMenuAgent(null);
    setActionInProgress(false);
  }, []);

  const handleOpenDetails = useCallback(() => {
    if (!menuAgent) return;
    setSelectedAgentForDetails(menuAgent);
    setDetailsModalOpen(true);
    handleCloseContextMenu();
  }, [menuAgent]);

  const handleStatusMenuClose = useCallback(() => {
    setStatusMenuPosition(null);
    setMenuAgent(null);
  }, []);

  const handleOpenStatusMenu = useCallback(() => {
    if (!contextMenuState || !menuAgent) return;
    setStatusMenuPosition({
      mouseX: contextMenuState.mouseX + 220,
      mouseY: contextMenuState.mouseY
    });
    setContextMenuState(null);
  }, [contextMenuState, menuAgent]);

  const handleViewScreen = useCallback(() => {
    if (!menuAgent) {
      handleCloseContextMenu();
      return;
    }
    setScreenViewerAgent(menuAgent);
    setScreenViewerOpen(true);
    handleCloseContextMenu();
  }, [menuAgent, handleCloseContextMenu]);

  const handleForceDisconnect = useCallback(async () => {
    if (!menuAgent) return;
    const agentId = menuAgent.agent;
    const confirmed = window.confirm(`¿Deseas desconectar al agente ${agentId}?`);
    if (!confirmed) {
      handleCloseContextMenu();
      return;
    }
    setContextMenuState(null);
    setStatusMenuPosition(null);
    setActionInProgress(true);
    try {
      await api.agents.forceDisconnect(agentId);
      showWarning(`Agente ${agentId} desconectado por supervisor.`);
      await loadAgents();
    } catch (error) {
      console.error('[SUPERVISOR] Error forcing disconnect', error);
      showError(error.message || 'No se pudo desconectar al agente');
    } finally {
      setActionInProgress(false);
      handleCloseContextMenu();
    }
  }, [api.agents, menuAgent, loadAgents, showError, showWarning, handleCloseContextMenu]);

  const handleRefreshAgent = useCallback(() => {
    if (!menuAgent) return;
    const agentId = menuAgent.agent;
    const confirmed = window.confirm(`¿Deseas refrescar la página del agente ${agentId}?`);
    if (!confirmed) {
      handleCloseContextMenu();
      return;
    }
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const signalUrl = `${protocol}://${window.location.host}/signal`;
      const ws = new WebSocket(signalUrl);
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'role', role: 'requester', id: agentId }));
        ws.send(JSON.stringify({ type: 'request', request: 'refresh', id: agentId }));
        setTimeout(() => {
          try { ws.close(); } catch {}
        }, 200);
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
    } catch (error) {
      showError('No se pudo enviar la solicitud de refresco');
    } finally {
      handleCloseContextMenu();
    }
  }, [menuAgent, handleCloseContextMenu, showError]);

  const handleStatusChange = useCallback(async (newStatus) => {
    if (!menuAgent) return;
    const agentId = menuAgent.agent;
    const currentStatus = menuAgent.status;

    setStatusMenuPosition(null);
    setActionInProgress(true);

    try {
      // 🔒 COLA DE ESTADOS: No permitir cambios si el agente está en INCALL o ACW
      // Solo permitir cambios desde estados de pausa o AVAILABLE
      const STATES_THAT_BLOCK_CHANGES = ['INCALL', 'ACW', 'PRECALL'];

      if (STATES_THAT_BLOCK_CHANGES.includes(currentStatus)) {
        showWarning(`No se puede cambiar el estado mientras el agente está en ${getAgentStatusLabel(currentStatus) || currentStatus}.`);

        // Enviar con expected_statuses para que se encole
        const allowedStates = currentStatus === 'INCALL'
          ? ['ACW']  // Si está en llamada, esperar a ACW
          : ['AVAILABLE', 'PAUSE'];  // Si está en ACW o PRECALL, esperar a disponible/pausa

        await api.agents.updateStatus(agentId, {
          status: newStatus,
          pause_code: '',
          expected_statuses: allowedStates,
          reason: 'supervisor_queued_change',
          metadata: {
            requestedBy: 'supervisor',
            queuedAt: new Date().toISOString(),
            currentState: currentStatus
          }
        });

        console.log(`[SUPERVISOR] Estado ${newStatus} encolado para ${agentId} (actual: ${currentStatus})`);
      } else {
        // Estado normal - ejecutar inmediatamente
        await api.agents.updateStatus(agentId, {
          status: newStatus,
          pause_code: '',
          reason: 'supervisor_immediate_change'
        });
        showSuccess(`Estado de ${agentId} actualizado a ${getAgentStatusLabel(newStatus) || newStatus}`);
      }

      await loadAgents();
    } catch (error) {
      console.error('[SUPERVISOR] Error on status change', error);
      showError(error.message || 'No se pudo cambiar el estado');
    } finally {
      setActionInProgress(false);
      handleCloseContextMenu();
    }
  }, [api.agents, menuAgent, loadAgents, showError, showSuccess, showWarning, handleCloseContextMenu]);

  const handleListenOption = useCallback(async () => {
    if (!menuAgent) {
      handleCloseContextMenu();
      return;
    }

    const supervisorId = (auth?.user?.agent || auth?.user?.username || auth?.user?.id || '').toString().trim();
    if (!supervisorId) {
      showError('No se pudo identificar tu usuario para iniciar la escucha.');
      handleCloseContextMenu();
      return;
    }

    if (!menuAgent.extension) {
      showError(`El agente ${menuAgent.agent} no tiene una extensión configurada.`);
      handleCloseContextMenu();
      return;
    }

    const normalizedStatus = menuAgent.status || '';
    if (!LISTENABLE_STATUSES.has(normalizedStatus)) {
      showInfo(`Solo puedes escuchar agentes en llamada. Estado actual: ${getAgentStatusLabel(normalizedStatus) || normalizedStatus || 'Sin estado'}.`);
      handleCloseContextMenu();
      return;
    }

    setActionInProgress(true);
    try {
      if (softphoneHangup) {
        try {
          await softphoneHangup();
        } catch (hangupError) {
          console.warn('[SUPERVISOR] No se pudo colgar la sesión previa antes de escuchar:', hangupError);
        }
      }

      const activeCall = await api.softphone.getActiveCall(menuAgent.agent);
      if (!activeCall?.active || !activeCall?.callId) {
        throw new Error('El agente no tiene una llamada activa para monitorear.');
      }
      setMonitoringSession({
        agentId: menuAgent.agent,
        agentName: menuAgent.name || menuAgent.agent,
        mode: 'whisper'
      });
    } catch (error) {
      console.error('[SUPERVISOR] Error iniciando escucha', error);
      clearMonitoringSession();
      showError(error.message || 'No se pudo iniciar la escucha');
    } finally {
      setActionInProgress(false);
      handleCloseContextMenu();
    }
  }, [menuAgent, auth?.user, api.supervisor, softphoneHangup, setMonitoringSession, clearMonitoringSession, showError, showInfo, showSuccess, handleCloseContextMenu]);

  const handleConferenceOption = useCallback(async () => {
    if (!menuAgent) {
      handleCloseContextMenu();
      return;
    }

    const supervisorId = (auth?.user?.agent || auth?.user?.username || auth?.user?.id || '').toString().trim();
    if (!supervisorId) {
      showError('No se pudo identificar tu usuario para iniciar la conferencia.');
      handleCloseContextMenu();
      return;
    }

    if (!menuAgent.extension) {
      showError(`El agente ${menuAgent.agent} no tiene una extensión configurada.`);
      handleCloseContextMenu();
      return;
    }

    const normalizedStatus = menuAgent.status || '';
    if (!LISTENABLE_STATUSES.has(normalizedStatus)) {
      showInfo(`Solo puedes iniciar conferencia cuando el agente está en llamada. Estado actual: ${getAgentStatusLabel(normalizedStatus) || normalizedStatus || 'Sin estado'}.`);
      handleCloseContextMenu();
      return;
    }

    setActionInProgress(true);
    try {
      if (softphoneHangup) {
        try {
          await softphoneHangup();
        } catch (hangupError) {
          console.warn('[SUPERVISOR] No se pudo colgar la sesión previa antes de la conferencia:', hangupError);
        }
      }

      const activeCall = await api.softphone.getActiveCall(menuAgent.agent);
      if (!activeCall?.active || !activeCall?.callId) {
        throw new Error('El agente no tiene una llamada activa para monitorear.');
      }
      setMonitoringSession({
        agentId: menuAgent.agent,
        agentName: menuAgent.name || menuAgent.agent,
        mode: 'conference'
      });
    } catch (error) {
      console.error('[SUPERVISOR] Error iniciando conferencia', error);
      clearMonitoringSession();
      showError(error.message || 'No se pudo iniciar la conferencia');
    } finally {
      setActionInProgress(false);
      handleCloseContextMenu();
    }
  }, [menuAgent, auth?.user, api.supervisor, softphoneHangup, setMonitoringSession, clearMonitoringSession, showError, showInfo, showSuccess, handleCloseContextMenu]);

  const isBubbleView = viewMode === 'bubble';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('immersive-mode-change', { detail: { active: isBubbleView } }));
    }
    return () => {
      if (isBubbleView && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('immersive-mode-change', { detail: { active: false } }));
      }
    };
  }, [isBubbleView]);

  return (
    <Box
      sx={{
        maxWidth: '100vw',
        minHeight: '100vh',
        overflow: 'hidden',
        boxSizing: 'border-box',
        px: 0, // el layout ya aporta 10px laterales; evitamos duplicarlo
        py: isBubbleView ? 0 : 2
      }}
    >
      {!isBubbleView && (
      <Card sx={{ mb: 1.5, position: 'relative' }}>
        <CardContent sx={{ p: { xs: 1, md: 1.25 } }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs>
              <Typography variant="h6" fontSize="1rem">
                Filtros
              </Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  color="inherit"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleClearFilters}
                  size="small"
                >
                  Limpiar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadAgents}
                  disabled={loading}
                  size="small"
                >
                  Actualizar
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ rowGap: 1.5 }}>
            {/* Primera fila - Filtros principales */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Buscar agente"
                value={filters.agent}
                onChange={handleFilterChange('agent')}
                placeholder="ID, nombre o extensión"
                sx={FILTER_CONTROL_SX}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  minWidth: 220,
                  ...FILTER_CONTROL_SX
                }}
              >
                <InputLabel id="column-selector-label">Columnas visibles</InputLabel>
                <Select
                  labelId="column-selector-label"
                  multiple
                  value={visibleColumns}
                  label="Columnas visibles"
                  onChange={handleColumnsChange}
                  renderValue={(selected) => {
                    if (!selected.length) {
                      return 'Ninguna';
                    }
                    if (selected.length === COLUMN_OPTIONS.length) {
                      return 'Todas las columnas';
                    }
                    return `${selected.length} columnas seleccionadas`;
                  }}
                >
                  {COLUMN_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={visibleColumns.includes(option.value)} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="filter-campaign-label">Campaña</InputLabel>
                <Select
                  labelId="filter-campaign-label"
                  value={filters.campaignId}
                  label="Campaña"
                  onChange={handleFilterChange('campaignId')}
                >
                  <MenuItem value="">
                    <em>Todas</em>
                  </MenuItem>
                  {campaigns.map(campaign => (
                    <MenuItem key={campaign.idcampaign} value={campaign.idcampaign}>
                      {campaign.descripcion || `Campaña ${campaign.idcampaign}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="filter-skill-label">Skill</InputLabel>
                <Select
                  labelId="filter-skill-label"
                  value={filters.skillId}
                  label="Skill"
                  onChange={handleFilterChange('skillId')}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {skills.map(skill => (
                    <MenuItem key={skill.id} value={skill.id}>
                      {skill.skillname} ({skill.skilldef})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="filter-status-label">Estado</InputLabel>
                <Select
                  labelId="filter-status-label"
                  value={filters.status}
                  label="Estado"
                  onChange={handleFilterChange('status')}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {statusOptions.map(status => (
                    <MenuItem key={status} value={status}>
                      {getAgentStatusLabel(status) || status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="filter-supervisor-label">Supervisor</InputLabel>
                <Select
                  labelId="filter-supervisor-label"
                  value={filters.supervisorId}
                  label="Supervisor"
                  onChange={handleFilterChange('supervisorId')}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {supervisorOptions.map((sup) => (
                    <MenuItem key={sup.id} value={sup.id}>
                      {sup.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                <InputLabel id="filter-manager-label">Gerente</InputLabel>
                <Select
                  labelId="filter-manager-label"
                  value={filters.managerId}
                  label="Gerente"
                  onChange={handleFilterChange('managerId')}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {managerOptions.map((mgr) => (
                    <MenuItem key={mgr.id} value={mgr.id}>
                      {mgr.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.hideDisconnected}
                      onChange={(e) => setFilters(prev => ({ ...prev, hideDisconnected: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Ocultar desconectados"
                  sx={{
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.onlyAgents}
                      onChange={(e) => setFilters(prev => ({ ...prev, onlyAgents: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Solo Agentes"
                  sx={{
                    whiteSpace: 'nowrap',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.85rem'
                    }
                  }}
                />
              </Stack>
            </Grid>

          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Segunda fila - Controles adicionales */}
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newView) => newView && setViewMode(newView)}
                size="small"
                sx={{
                  flexWrap: 'wrap',
                  width: '100%',
                  justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                }}
              >
                <ToggleButton value="table">
                  <ViewListIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Tabla
                </ToggleButton>
                <ToggleButton value="grid">
                  <GridViewIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Cuadrícula
                </ToggleButton>
                <ToggleButton value="bubble">
                  <BubbleChartIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Burbujas
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} md={8}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                alignItems={{ xs: 'flex-start', sm: 'center', md: 'flex-end' }}
                sx={{ height: '100%' }}
              >
                <Typography variant="caption" color="text.secondary">
                  Última actualización:
                </Typography>
                <Typography variant="caption">
                  {lastUpdated ? new Date(lastUpdated).toLocaleString() : 'Sin datos'}
                </Typography>
              </Stack>
            </Grid>
          </Grid>

        </CardContent>
        <Box sx={{ height: 4 }}>
          {loading && <LinearProgress />}
        </Box>
      </Card>
      )}
      {!isBubbleView && (
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <Card elevation={0} sx={{ ...KPI_CARD_SX, height: '100%' }}>
            <CardContent sx={{ ...KPI_CARD_CONTENT_SX, minHeight: 80 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Agentes
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                {statusSummary.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card elevation={0} sx={{ ...KPI_CARD_SX, height: '100%' }}>
            <CardContent sx={{ ...KPI_CARD_CONTENT_SX, minHeight: 80 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Conectados
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircleIcon color="success" fontSize="small" />
                <Typography variant="h4" fontWeight={600}>
                  {statusSummary.connected}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card elevation={0} sx={{ ...KPI_CARD_SX, height: '100%' }}>
            <CardContent sx={{ ...KPI_CARD_CONTENT_SX, minHeight: 80 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Disponibles
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircleIcon fontSize="small" sx={{ color: getAgentStatusColor('AVAILABLE') }} />
                <Typography variant="h4" fontWeight={600}>
                  {statusSummary.available}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card elevation={0} sx={{ ...KPI_CARD_SX, height: '100%' }}>
            <CardContent sx={{ ...KPI_CARD_CONTENT_SX, minHeight: 80 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                En Llamada
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CallIcon fontSize="small" sx={{ color: getAgentStatusColor('INCALL') }} />
                <Typography variant="h4" fontWeight={600}>
                  {statusSummary.inCallGroup}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card elevation={0} sx={{ ...KPI_CARD_SX, height: '100%' }}>
            <CardContent sx={{ ...KPI_CARD_CONTENT_SX, minHeight: 80 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                En Pausa
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PauseCircleIcon fontSize="small" sx={{ color: getAgentStatusColor('PAUSE') }} />
                <Typography variant="h4" fontWeight={600}>
                  {statusSummary.paused}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card elevation={0} sx={{ ...KPI_CARD_SX, height: '100%' }}>
            <CardContent sx={{ ...KPI_CARD_CONTENT_SX, minHeight: 80 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Llamadas
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CallIcon color="primary" fontSize="small" />
                <Typography variant="h4" fontWeight={600}>
                  {statusSummary.totalCalls}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {isBubbleView ? (
        <SpaceBubbleView
          agents={sortedAgents}
          now={now}
          onContextMenu={handleAgentContextMenu}
          immersive
          onExitImmersive={() => setViewMode('table')}
        />
      ) : viewMode === 'grid' ? (
        <Box>
          {/* Grid de agentes */}
          <Grid container spacing={2}>
            {sortedAgents.map(agent => {
              const statusInfo = {
                label: getAgentStatusLabel(agent.status) || agent.status,
                color: getAgentStatusColor(agent.status)
              };
              const durationSeconds = getStatusDurationSeconds(agent, now);
              const connected = agent.connection?.connected;
              const timeDisplay = formatDuration(durationSeconds);

              return (
                <Grid item xs={6} sm={4} md={3} lg={2} xl={2} key={agent.agent}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 140,
                      background: getAgentStatusGradient(agent.status),
                      borderRadius: 2,
                      overflow: 'hidden',
                      cursor: connected ? 'pointer' : 'default',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                      '&:hover': connected ? {
                        transform: 'scale(1.05)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
                        zIndex: 10
                      } : {},
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        pointerEvents: 'none'
                      }
                    }}
                    onContextMenu={(event) => handleAgentContextMenu(event, agent, connected)}
                    onClick={() => handleAgentClick(agent)}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        height: '100%',
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        color: getAgentStatusTextColor(agent.status)
                      }}
                    >
                      {/* Header con ID */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.7rem',
                            fontWeight: 500
                          }}
                        >
                          ID: {agent.agent}
                        </Typography>
                        {!connected && (
                          <CircleIcon
                            sx={{
                              fontSize: 8,
                              color: 'rgba(255, 255, 255, 0.5)'
                            }}
                          />
                        )}
                      </Stack>

                      {/* Nombre del agente */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: 'white',
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {agent.name || 'Sin nombre'}
                      </Typography>

                      {/* Estado */}
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 'auto' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.9)'
                          }}
                        >
                          {statusInfo.label}
                        </Typography>
                        {agent.callDirection === 'inbound' && (
                          <Tooltip title="Entrante">
                            <CallReceivedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }} />
                          </Tooltip>
                        )}
                        {agent.callDirection === 'outbound' && (
                          <Tooltip title="Saliente">
                            <CallMadeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }} />
                          </Tooltip>
                        )}
                      </Stack>

                      {/* Tiempo en display de 7 segmentos */}
                      <Typography
                        sx={{
                          fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                          fontFamily: '"DSEG7", monospace',
                          fontWeight: 400,
                          letterSpacing: '0.1em',
                          color: '#00ff00',
                          textAlign: 'center',
                          mt: 'auto',
                          textShadow: '0 0 15px rgba(0, 255, 0, 0.8), 0 0 5px rgba(0, 255, 0, 0.5)',
                          background: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: 1,
                          px: 0.5,
                          py: 0.5,
                          fontVariantNumeric: 'tabular-nums',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid rgba(0, 255, 0, 0.2)'
                        }}
                      >
                        {timeDisplay}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}

            {sortedAgents.length === 0 && (
              <Grid item xs={12}>
                <Box py={4} textAlign="center">
                  <Typography variant="subtitle1" color="text.secondary">
                    No hay agentes que coincidan con los filtros seleccionados.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ajusta la búsqueda o limpia los filtros para ver todos los agentes.
                  </Typography>
                  <IconButton color="primary" onClick={handleClearFilters}>
                    <FilterAltOffIcon />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : (
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10
          }}
        >
          <Tooltip title="Control de Zoom">
            <IconButton
              size="small"
              onClick={(e) => setZoomAnchorEl(e.currentTarget)}
              sx={{
                backgroundColor: 'background.paper',
                boxShadow: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: 0,
            minWidth: '100%',
            overflowX: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            backgroundColor: 'background.paper'
          }}
        >
        <Table
          sx={{
            minWidth: 'max-content',
            transform: `scale(${tableZoom / 100})`,
            transformOrigin: 'top left',
            width: `${(100 / tableZoom) * 100}%`,
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap'
            }
          }}
        >
            <TableHead>
              <TableRow>
                {COLUMN_OPTIONS.map(column => (
                  isColumnVisible(column.value) && (
                    <TableCell
                      key={column.value}
                      align="left"
                    >
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {column.sortable ? (
                            <TableSortLabel
                              active={sortConfig.column === column.value}
                              direction={sortConfig.column === column.value ? sortConfig.direction : 'asc'}
                              onClick={() => handleSortChange(column.value)}
                            >
                              {column.header}
                            </TableSortLabel>
                          ) : (
                            <Typography variant="subtitle2">
                              {column.header}
                            </Typography>
                          )}
                          {column.filterable && (
                            <IconButton
                              size="small"
                              color={columnFilters[column.value] ? 'primary' : 'default'}
                              onClick={() =>
                                setActiveFilterColumn(prev =>
                                  prev === column.value ? null : column.value
                                )}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                        {column.filterable && activeFilterColumn === column.value && (
                          <TextField
                            value={columnFilters[column.value] || ''}
                            onChange={(event) => handleColumnFilterChange(column.value, event.target.value)}
                            size="small"
                            placeholder="Filtrar"
                            variant="standard"
                            autoFocus
                            onBlur={() => setActiveFilterColumn(null)}
                            onKeyDown={(event) => {
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                setActiveFilterColumn(null);
                              }
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  )
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAgents.map(agent => {
                const statusInfo = {
                  label: getAgentStatusLabel(agent.status) || agent.status,
                  color: getAgentStatusColor(agent.status)
                };
                const durationSeconds = getStatusDurationSeconds(agent, now);
                const connectionSeconds = getConnectionSeconds(agent, now);
                const connected = agent.connection?.connected;

                return (
                  <TableRow
                    key={agent.agent}
                    hover
                    onContextMenu={(event) => handleAgentContextMenu(event, agent, connected)}
                    onClick={() => handleAgentClick(agent)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {isColumnVisible('indicator') && (
                      <TableCell sx={{ width: 56 }}>
                        <Tooltip
                          title={
                            connected
                              ? `Conectado vía heartbeat (${statusInfo.label})`
                              : `Desconectado (${agent.connection?.disconnectReason || 'sin motivo'})`
                          }
                        >
                          <CircleIcon
                            fontSize="small"
                            sx={connected ? { color: statusInfo.color } : undefined}
                            color={connected ? undefined : 'disabled'}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    {isColumnVisible('agentId') && (
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {agent.agent}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('name') && (
                      <TableCell>
                        <Typography variant="subtitle2">
                          {agent.name || 'Sin nombre'}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('extension') && (
                      <TableCell>
                        <Typography variant="body2">
                          {agent.extension || 'N/A'}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('status') && (
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Chip
                            size="small"
                            label={statusInfo.label}
                            sx={{
                              backgroundColor: statusInfo.color,
                              color: getAgentStatusTextColor(agent.status)
                            }}
                          />
                          {agent.callDirection === 'inbound' && (
                            <Tooltip title="Entrante (automática)">
                              <CallReceivedIcon sx={{ fontSize: 16, color: statusInfo.color }} />
                            </Tooltip>
                          )}
                          {agent.callDirection === 'outbound' && (
                            <Tooltip title="Saliente (manual)">
                              <CallMadeIcon sx={{ fontSize: 16, color: statusInfo.color }} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                    {isColumnVisible('stateChanges') && (
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {agent.stateChangeCount || 0}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('callCount') && (
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {agent.callCountToday || 0}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('time') && (
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WatchLaterIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontFamily="monospace">
                            {formatDuration(durationSeconds)}
                          </Typography>
                        </Stack>
                      </TableCell>
                    )}
                    {isColumnVisible('connectionTime') && (
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {formatDuration(connectionSeconds)}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('firstConnection') && (
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(agent.firstConnectionAt)}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('lastDisconnect') && (
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(agent.lastDisconnectAt)}
                        </Typography>
                      </TableCell>
                    )}
                    {isColumnVisible('skills') && (
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {(agent.skills || []).map(skill => (
                            <Chip
                              key={`${agent.agent}-${skill.id}`}
                              size="small"
                              label={`${skill.name} (${skill.def})`}
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                          {(agent.skills || []).length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Sin skills activos
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                    {isColumnVisible('campaigns') && (
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {(agent.campaigns || []).map(id => (
                            <Chip
                              key={`${agent.agent}-campaign-${id}`}
                              size="small"
                              variant="outlined"
                              label={campaignLookup.get(String(id)) || `Campaña ${id}`}
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                          {(agent.campaigns || []).length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Sin Campaña
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}

              {!loading && columnFilteredAgents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length || 1}>
                    <Box py={4} textAlign="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        No hay agentes que coincidan con los filtros seleccionados.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ajusta la búsqueda o limpia los filtros para ver todos los agentes.
                      </Typography>
                      <IconButton color="primary" onClick={handleClearFilters}>
                        <FilterAltOffIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
        </Table>
      </Box>
      </Box>
      )}

      <Popover
        open={Boolean(zoomAnchorEl)}
        anchorEl={zoomAnchorEl}
        onClose={() => setZoomAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            Control de Zoom
          </Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={tableZoom <= 50}
              >
                <ZoomOutIcon />
              </IconButton>
              <Box sx={{ flex: 1, mx: 2 }}>
                <Typography variant="body2" textAlign="center">
                  {tableZoom}%
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={tableZoom >= 150}
              >
                <ZoomInIcon />
              </IconButton>
            </Stack>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={handleZoomReset}
              disabled={tableZoom === 100}
            >
              Restablecer (100%)
            </Button>
          </Stack>
        </Box>
      </Popover>

      <Menu
        open={Boolean(contextMenuState)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuState
            ? { top: contextMenuState.mouseY, left: contextMenuState.mouseX }
            : undefined
        }
        keepMounted
      >
        {canAction('change_status_supervisor') && (
        <MenuItem onClick={handleOpenStatusMenu} disabled={!menuAgent || actionInProgress}>
          <ListItemIcon>
            <SyncAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Cambiar estado" secondary="Selecciona un estado" />
          <ChevronRightIcon fontSize="small" />
        </MenuItem>
        )}
        <MenuItem onClick={handleOpenDetails} disabled={!menuAgent || actionInProgress}>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Ver Detalles" secondary="Estadísticas del día" />
        </MenuItem>
        <Divider />
        {canAction('refresh_agent_supervisor') && (
        <MenuItem onClick={handleRefreshAgent} disabled={!menuAgent || actionInProgress}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Refrescar" />
        </MenuItem>
        )}
        {canAction('disconnect_supervisor') && (
        <MenuItem onClick={handleForceDisconnect} disabled={!menuAgent || actionInProgress}>
          <ListItemIcon>
            <PowerSettingsNewIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Desconectar agente" />
        </MenuItem>
        )}
        {canAction('view_screen_supervisor') && (
        <MenuItem onClick={handleViewScreen} disabled={!menuAgent || actionInProgress}>
          <ListItemIcon>
            <MonitorIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Ver Pantalla" />
        </MenuItem>
        )}
        {canAction('spy_supervisor') && (
        <MenuItem
          onClick={handleListenOption}
          disabled={
            !menuAgent ||
            actionInProgress ||
            !menuAgent.extension ||
            !LISTENABLE_STATUSES.has(menuAgent.status || '')
          }
        >
          <ListItemIcon>
            <HeadsetMicIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Escuchar" />
        </MenuItem>
        )}
        {canAction('conference_supervisor') && (
        <MenuItem
          onClick={handleConferenceOption}
          disabled={
            !menuAgent ||
            actionInProgress ||
            !menuAgent.extension ||
            !LISTENABLE_STATUSES.has(menuAgent.status || '')
          }
        >
          <ListItemIcon>
            <RecordVoiceOverIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText primary="Conferencia" />
        </MenuItem>
        )}
      </Menu>

      <Menu
        open={Boolean(statusMenuPosition)}
        onClose={handleStatusMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          statusMenuPosition
            ? { top: statusMenuPosition.mouseY, left: statusMenuPosition.mouseX }
            : undefined
        }
        keepMounted
      >
        {manualStatusOptions.map((status) => {
          const current = menuAgent?.status;
          const isCurrent = current === status;
          const statusColor = getAgentStatusColor(status);
          return (
            <MenuItem
              key={status}
              onClick={() => handleStatusChange(status)}
              disabled={!menuAgent || actionInProgress || isCurrent}
            >
              <ListItemIcon>
                <CircleIcon
                  fontSize="small"
                  sx={{ color: isCurrent ? 'primary.main' : statusColor }}
                />
              </ListItemIcon>
              <ListItemText
                primary={getAgentStatusLabel(status) || status}
                secondary={isCurrent ? 'Actual' : null}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Panel lateral rápido del agente */}
      <AgentQuickPanel
        open={!!quickPanelAgent}
        onClose={() => setQuickPanelAgent(null)}
        agent={quickPanelAgent}
        campaignLookup={campaignLookup}
      />

      {/* Modal de Detalles del Agente */}
      <AgentDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        agentId={selectedAgentForDetails?.agent}
        agentName={selectedAgentForDetails?.name}
      />

      <Dialog
        open={screenViewerOpen}
        onClose={() => setScreenViewerOpen(false)}
        fullScreen
        PaperProps={{ sx: { overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 1, backgroundColor: '#000', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
              Ver Pantalla
              {screenViewerAgent?.agent ? ` - ${screenViewerAgent.agent}` : ''}
              {screenViewerAgent?.name ? ` - ${screenViewerAgent.name}` : ''}
            </Typography>
            <IconButton onClick={() => setScreenViewerOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers={false} sx={{ p: 0, backgroundColor: '#000', overflow: 'hidden' }}>
          {screenViewerAgent?.agent ? (
            <iframe
              title="Screen Viewer"
              src={`/viewer?id=${encodeURIComponent(screenViewerAgent.agent)}&quality=high`}
              style={{ width: '100%', height: 'calc(100vh - 56px)', border: 'none', display: 'block' }}
              allow="autoplay"
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2">No se pudo identificar el agente.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupervisorAgentsPage;





